// @ts-nocheck
'use strict';

// SVG icons are inlined in the section via hidden <template> (see bundle-configurator.liquid).
// Read innerHTML once at script load (defer — DOM is already parsed).
const _bcIcon = (id) => document.getElementById(id)?.innerHTML ?? '';
const _BC_ARROW_SVG = _bcIcon('bc-icon-arrow');
const _BC_ATC_ICON  = _bcIcon('bc-icon-add-to-cart');
const _BC_CHECK_SVG = _bcIcon('bc-icon-checkmark');

function bcCdnResize(src, size) {
  return src ? src.replace(/(\.[^.?]+)(\?.*)?$/, `_${size}x$1$2`) : src;
}

function bcFormatMoney(cents, currencyCode) {
  return new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: currencyCode || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

class BundleConfigurator extends HTMLElement {

  connectedCallback() {
    const dataScript = document.getElementById('bundle-data');
    if (!dataScript) { console.error('[BC] #bundle-data not found'); return; }

    try {
      this._data = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('[BC] JSON parse error:', e.message);
      return;
    }

    this._t        = this._data.translations;
    this._currency = this._data.currency;
    this._imgSize      = this._data.image_size || 900;
    this._imgSize2x    = this._imgSize * 2;
    this._bcRetailVal  = this._data.bc_retail_value || 'Retail';
    this._imgHeight = this._data.image_height || this._imgSize;

    this._state = {
      currentStep:         1,
      selectedBase:              null,
      selectedBaseVariant:       null,
      selectedMood:              null,
      selectedLargePatches:      [],
      selectedExtraLargePatches: [],
      extraLargeSlotCount:       0,
      selectedMinis:             [],
      selectedExtraMiniPatches:  [],
      extraMiniSlotCount:        0,
      selectedAdds:              [],
      selectedExtraAdds:         [],
      extraAddsSlotCount:        0,
      optionSelections:    {},
      lastOptionValues:    {}, // { "Size": "L", "Color": "Black" } — cross-product memory
      patchesCache:        {}, // { collectionHandle: [product, ...] }
      // Final payload for /cart/add.js — assembled as the user goes through the steps.
      // Slot 'bundle' — the MoodBox itself (parent), the rest are components.
      cartItems: [
        {
          _slot:      'bundle',
          id:         this._data.product.default_variant_id,
          quantity:   1,
          properties: {},
        },
      ],
    };

    // Regions (persistent): content — right column, aside — bundle contents (left),
    // panel — bottom panel (loader/options/total/buttons). Queries are scoped per region.
    this._mediaEl  = this.querySelector('.js-bundle-media');
    this._panelsEl = this.querySelector('.js-bundle-panels');
    this._asideEl  = this.querySelector('.js-bundle-aside');
    this._panelEl  = this.querySelector('.js-bundle-panel');

    this._initStep1Events();
    this._initInfoUI();
    if (this.dataset.autoscroll === 'true') this._initAutoScroll();
    if (this.dataset.scrollOnLoad === 'true') this._scrollSectionIntoView();
    if (this.dataset.panelDelayed === 'true') this._initPanelReveal();
    this._initMobileRail();
  }

  // Delayed bottom panel: hidden until the section is >= N% visible, then revealed
  // (optionally sliding up). Hidden again when the visitor leaves, so it re-animates.
  _initPanelReveal() {
    const panel = this._panelEl;
    if (!panel) return;
    // Initial hidden state comes from the [data-panel-delayed] CSS (no flash).
    const pct = Math.min(Math.max(parseInt(this.dataset.panelRevealPct, 10) || 50, 5), 95) / 100;
    const sectionEl = this.closest('.shopify-section') || this;

    // The panel sits at the section bottom. One positional rule covers both
    // directions: show while the section is on screen AND its top edge has risen
    // above the reveal line (vh * (1 - pct)); hide otherwise.
    //   scroll down: top crosses the line at `pct` visible → appears at pct,
    //                stays until the section fully leaves the top → hides at 100%.
    //   scroll up:   section enters bottom-first, top is already above the line →
    //                visible at 0%, hides once the top drops back below the line.
    let ticking = false;
    const evaluate = () => {
      ticking = false;
      const rect = sectionEl.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      const onScreen = rect.bottom > 0 && rect.top < vh;
      const pastRevealLine = rect.top <= vh * (1 - pct);
      panel.classList.toggle('bc-panel--shown', onScreen && pastRevealLine);
    };
    this._panelOnScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(evaluate);
    };
    // Only sample scroll position while the section is anywhere near the viewport.
    this._panelIO = new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];
      if (!e) return;
      if (e.isIntersecting) {
        window.addEventListener('scroll', this._panelOnScroll, { passive: true });
        window.addEventListener('resize', this._panelOnScroll, { passive: true });
      } else {
        window.removeEventListener('scroll', this._panelOnScroll);
        window.removeEventListener('resize', this._panelOnScroll);
      }
      evaluate();
    }, { threshold: [0, 1] });
    this._panelIO.observe(sectionEl);
  }

  // ── Section auto-align (single source of truth for page scrolling) ──────
  // _sectionScrollDelta is THE offset math; every window-scroll below uses it.
  // Distance (px) to scroll so the section top aligns with the header offset.
  // Positive = the section sits below the ideal top.
  _sectionScrollDelta() {
    const sectionEl = this.closest('.shopify-section') || this;
    return sectionEl.getBoundingClientRect().top - this._stickyHeaderOffset();
  }

  // Height of what actually STICKS while scrolling. --header-group-height includes
  // the announcement bar, but that scrolls away (home page especially) — only the
  // header itself stays, so aligning to the group height leaves a gap / cuts text.
  _stickyHeaderOffset() {
    if (window.matchMedia('(max-width: 749px)').matches) return 0;   // mobile overlaps the header
    const styles = getComputedStyle(document.body);
    return parseInt(styles.getPropertyValue('--header-height'))
      || parseInt(styles.getPropertyValue('--header-group-height'))
      || 108;
  }

  // Pull the section fully into view. down-only (default) ignores negative deltas,
  // so it never yanks the page up while the visitor is reading above the section.
  // The feature stays ON with prefers-reduced-motion — only the animation is
  // dropped (instant jump instead of smooth).
  _alignSection(bothDirections = false) {
    const delta = this._sectionScrollDelta();
    if (delta > 4 || (bothDirections && delta < -4)) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollBy({ top: delta, behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  // scroll-on-load: after layout/images settle, scroll straight down to the section
  _scrollSectionIntoView() {
    requestAnimationFrame(() => setTimeout(() => {
      if (document.querySelector('dialog[open]')) return;   // don't fight an open modal
      this._alignSection();
    }, 250));
  }

  // bc_autoscroll: when most of the section enters the viewport, gently pull it the
  // rest of the way. IntersectionObserver (fires only at thresholds) instead of a
  // persistent window scroll listener; no direction tracking needed — the delta > 4
  // guard already limits pulls to "section still below its target".
  _initAutoScroll() {
    const sectionEl = this.closest('.shopify-section') || this;
    this._snapDone = false;
    this._snapIO = new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];   // entries can batch — use the newest
      if (!e) return;
      if (e.intersectionRatio < 0.35) { this._snapDone = false; return; }  // left → re-arm
      if (e.intersectionRatio < 0.55 || this._snapDone) return;
      if (this._autoScrollTimer) clearTimeout(this._autoScrollTimer);
      this._autoScrollTimer = setTimeout(() => {               // let the user's scroll settle
        if (document.querySelector('dialog[open]')) return;
        const d = this._sectionScrollDelta();
        // Pull down toward the section, or gently back after a SMALL overshoot
        // (heading slid under the sticky site header). A large negative delta
        // means the visitor is leaving — never fight that.
        if (d > 4 || (d < -4 && d > -80)) {
          this._snapDone = true;
          this._alignSection(true);
        }
      }, 150);
    }, { threshold: [0.35, 0.55] });
    this._snapIO.observe(sectionEl);
  }

  // Theme editor re-renders sections (shopify:section:load) — release timers
  // and observers so they do not pile up on detached instances.
  disconnectedCallback() {
    if (this._autoScrollTimer) { clearTimeout(this._autoScrollTimer); this._autoScrollTimer = null; }
    if (this._railScrollTimer) { clearTimeout(this._railScrollTimer); this._railScrollTimer = null; }
    if (this._railScrollRaf)   { cancelAnimationFrame(this._railScrollRaf); this._railScrollRaf = null; }
    this._galleryRO?.disconnect();       this._galleryRO = null;
    this._galleryThumbMO?.disconnect();  this._galleryThumbMO = null;
    this._galleryVideoIO?.disconnect();  this._galleryVideoIO = null;
    this._railMO?.disconnect();          this._railMO = null;
    this._snapIO?.disconnect();          this._snapIO = null;
    this._panelIO?.disconnect();         this._panelIO = null;
    if (this._panelOnScroll) {
      window.removeEventListener('scroll', this._panelOnScroll);
      window.removeEventListener('resize', this._panelOnScroll);
      this._panelOnScroll = null;
    }
    document.body.style.overflow = '';   // in case the popup was open
  }

  // ── Step 1 event wiring (DOM already rendered by Liquid) ──────────────────

  _initStep1Events() {
    // Card clicks
    this._panelsEl.querySelectorAll('.bundle-configurator__base-card').forEach((card) => {
      card.addEventListener('click', () => {
        const pid     = String(card.dataset.productId);
        const product = this._data.base_products.find((p) => String(p.id) === pid);
        if (product) this._onBaseCardClick(product, card);
      });
    });

    // Card "Details" buttons (desktop) — open the shared info dialog, don't select the card
    this._panelsEl.querySelectorAll('.js-base-info-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const pid     = String(btn.dataset.productId);
        const product = this._data.base_products.find((p) => String(p.id) === pid);
        this._openInfoDialog(product);
      });
    });

    // Chip clicks — stopPropagation so card click doesn't also fire
    this.querySelectorAll('.bundle-configurator__option-chip').forEach((chip) => {
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        const panel   = chip.closest('.bundle-configurator__variant-panel');
        const pid     = String(panel.dataset.productId);
        const product = this._data.base_products.find((p) => String(p.id) === pid);
        const row     = chip.closest('.bundle-configurator__option-values');
        if (product) this._onOptionChipClick(product, chip, row);
      });
    });

    // Continue
    const btn = this.querySelector('.js-continue-btn');
    if (btn) btn.addEventListener('click', () => this._onStep1Continue());

    // Buy only this
    const buyOnlyBtn = this.querySelector('.js-buy-only-btn');
    if (buyOnlyBtn) buyOnlyBtn.addEventListener('click', () => this._onBuyOnlyThis());

    // Auto-select first card on load
    const firstCard = this._panelsEl.querySelector('.bundle-configurator__base-card');
    if (firstCard) {
      const pid     = String(firstCard.dataset.productId);
      const product = this._data.base_products.find((p) => String(p.id) === pid);
      if (product) this._onBaseCardClick(product, firstCard);
    }
  }

  // ── Product details popup ─────────────────────────────────────────────
  // One shared <dialog-component id="bc-product-info-dialog">. The Description body
  // is swapped per product; Care / Shipping are static (section settings).

  _initInfoUI() {
    // Mobile: collapsible step description
    const toggle = this.querySelector('.js-desc-toggle');
    const acc    = this.querySelector('.js-desc-acc');
    if (toggle && acc) {
      toggle.addEventListener('click', () => {
        const open = acc.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    // Mobile: "Product Details" opens the dialog for whatever base is in the gallery
    const mBtn = this.querySelector('.js-info-open-mobile');
    if (mBtn) mBtn.addEventListener('click', () => this._openInfoDialog(this._infoProduct));
  }

  // Records the item currently in the gallery if it has a description (base, mood or add),
  // null otherwise (placeholder), and shows/hides the mobile info button accordingly.
  _setInfoProduct(product) {
    const hasDesc = !!(product && (product.description || '').trim());
    this._infoProduct = hasDesc ? product : null;
    const mBtn = this.querySelector('.js-info-open-mobile');
    // Position is fixed in CSS (mobile media is a constant 240px); only toggle visibility.
    if (mBtn) mBtn.hidden = !this._infoProduct;
  }

  // Shows + wires the "Details" button on a mood/add card when the item has a description.
  // The button lives outside .patch-card, so it never triggers card selection.
  _wireCardInfo(frag, item) {
    const btn = frag.querySelector('.js-patch-info-btn');
    if (!btn) return;
    const desc = (item.description || '').trim();
    if (!desc) return;
    btn.hidden = false;
    btn.addEventListener('click', (e) => { e.stopPropagation(); this._openInfoDialog(item); });
  }

  _openInfoDialog(product) {
    if (!product) return;
    const dlg = document.getElementById('bc-product-info-dialog');
    if (!dlg || typeof dlg.showDialog !== 'function') return;

    // Description — per product
    const body = dlg.querySelector('.js-info-desc');
    const desc = (product.description || '').trim();
    if (body) body.innerHTML = desc;
    const descAcc = body && body.closest('.bc-info-acc');
    if (descAcc) descAcc.hidden = !desc;   // hide the whole Description block if empty

    // Care — per step. Moods (step 2) fall back to the base content; adds (step 3)
    // have no care and never fall back. Empty → the Care section is hidden.
    const careEl = dlg.querySelector('.js-info-care');
    if (careEl) {
      const map  = this._data.info_care || {};
      const step = String(this._state.currentStep);
      let care = (map[step] || '').trim();
      if (!care && step === '2') care = (map['1'] || '').trim();
      careEl.innerHTML = care;
      const careAcc = careEl.closest('.bc-info-acc');
      if (careAcc) careAcc.hidden = !care;
    }

    dlg.showDialog();
  }

  // ── Mobile rail + Your MoodBox popup (steps 2-3) ──────────────────────────
  // The rail mirrors the live aside slots as visual clones (a MutationObserver
  // keeps it in sync with every slot change). The popup MOVES the real aside
  // node into itself, so all interactivity (remove X, extra buttons) stays live.

  _initMobileRail() {
    this._railEl  = this.querySelector('.js-bc-rail');
    this._popupEl = this.querySelector('.js-bc-popup');
    if (!this._railEl || !this._popupEl || !this._asideEl) return;
    this._railSlotsEl = this._railEl.querySelector('.js-bc-rail-slots');
    this._railCountEl = this._railEl.querySelector('.js-bc-rail-count');

    this._railEl.querySelector('.js-bc-rail-open')?.addEventListener('click', () => this._openMoodboxPopup());
    this._popupEl.querySelector('.js-bc-popup-close')?.addEventListener('click', () => this._closeMoodboxPopup());
    this._popupEl.querySelector('.js-bc-popup-continue')?.addEventListener('click', () => this._closeMoodboxPopup());

    let raf = null;
    this._railMO = new MutationObserver(() => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        this._syncMobileRail();
        this._scrollAsideRow();
      });
    });
    this._railMO.observe(this._asideEl, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  // Desktop: the aside slots are a single horizontal scroller — keep the
  // nearest unfilled slot in view (or the end when everything is filled)
  _scrollAsideRow() {
    if (this._popupEl && !this._popupEl.hidden) return;   // popup shows a grid, no row scroll
    const row = this._asideEl?.querySelector('.bundle-configurator__footer-slots');
    if (!row || row.scrollWidth <= row.clientWidth) return;
    const firstEmpty = row.querySelector('.bundle-configurator__patch-slot--empty');
    const target = firstEmpty
      ? Math.max(firstEmpty.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft - 48, 0)
      : row.scrollWidth;
    row.scrollTo({ left: target, behavior: 'smooth' });
  }

  _syncMobileRail() {
    this._updateSelectedCount();   // slots changed → refresh the mobile counter too
    this._updateRailRing();        // and the segmented progress ring on the thumb
    const rail = this._railEl;
    if (!rail) return;
    const step  = this._state.currentStep;
    const slots = (step === 1 || step === 2 || step === 3)
      ? this._asideEl.querySelectorAll('.bundle-configurator__patch-slot')
      : [];
    if (!slots.length) { rail.hidden = true; return; }
    rail.hidden = false;

    const wrap = this._railSlotsEl;

    // Rebuild only when the slots actually changed — the MutationObserver also fires
    // for unrelated aside mutations (button states etc.), and a full wipe resets the
    // scroll position and repaints every clone, which reads as a flicker.
    const sig = Array.from(slots).map((s) => {
      const img = s.querySelector('.bundle-configurator__patch-slot-img');
      return s.className + '|' + (img && !img.hidden ? img.src : '');
    }).join(';');
    let filled = 0;
    let firstEmptyIdx = -1;
    slots.forEach((slot, i) => {
      if (slot.classList.contains('bundle-configurator__patch-slot--filled')) filled++;
      if (firstEmptyIdx === -1 && slot.classList.contains('bundle-configurator__patch-slot--empty')) firstEmptyIdx = i;
    });

    if (sig !== this._railSig) {
      this._railSig = sig;
      // Reconcile in place: rail nodes are persistent — only the slot that actually
      // changed is touched (the "?" frame stays, the image fades in over it).
      while (wrap.children.length > slots.length) wrap.lastElementChild.remove();
      slots.forEach((slot, i) => {
        let node = wrap.children[i];
        if (!node) {
          node = slot.cloneNode(true);
          node.querySelector('.bundle-configurator__patch-slot-remove')?.remove();
          // One persistent listener; the source slot ref is refreshed on every sync
          node.addEventListener('click', () => {
            if (node._bcSrc?.classList.contains('bundle-configurator__patch-slot--filled')) node._bcSrc.click();
          });
          wrap.appendChild(node);
        }
        node._bcSrc = slot;
        if (node.className !== slot.className) node.className = slot.className;
        if (slot.dataset.slotType) node.dataset.slotType = slot.dataset.slotType;
        node.style.cursor = slot.classList.contains('bundle-configurator__patch-slot--filled') ? 'pointer' : '';

        const sImg = slot.querySelector('.bundle-configurator__patch-slot-img');
        const nImg = node.querySelector('.bundle-configurator__patch-slot-img');
        if (sImg && nImg) {
          const want = sImg.hidden ? '' : sImg.src;
          const have = nImg.hidden ? '' : nImg.src;
          if (want !== have) {
            if (want) {
              nImg.src = want;
              nImg.alt = sImg.alt || '';
              nImg.hidden = false;
              nImg.classList.add('bc-rail-img-in');
              nImg.addEventListener('animationend', () => nImg.classList.remove('bc-rail-img-in'), { once: true });
            } else {
              nImg.hidden = true;
              nImg.removeAttribute('src');
            }
          }
        }
      });
    }
    if (this._railCountEl) this._railCountEl.textContent = filled;

    // Scroll so the nearest unfilled slot is visible: short pause after the item
    // appears, then an unhurried eased glide (native smooth is too quick here)
    if (this._railScrollTimer) clearTimeout(this._railScrollTimer);
    this._railScrollTimer = setTimeout(() => {
      // Only the base is filled (fresh step 2) → show it in full at the top;
      // start gliding to the nearest empty slot once something gets added
      if (filled <= 1) { this._animateRailScroll(wrap, 0, 650); return; }
      const firstEmpty = firstEmptyIdx >= 0 ? wrap.children[firstEmptyIdx] : null;
      const target = firstEmpty ? Math.max(firstEmpty.offsetTop - 32, 0) : wrap.scrollHeight;
      this._animateRailScroll(wrap, target, 650);
    }, 400);
  }

  // Segmented progress ring around the rail thumb: one sector per planned bundle
  // slot (FULL plan: base + all mood slots + all adds slots, extras included),
  // colored by step; unfilled sectors are gray. 4deg gap ≈ 2px on this radius.
  _updateRailRing() {
    const thumb = this._railEl?.querySelector('.bc-rail__thumb');
    if (!thumb) return;
    const s    = this._state;
    const mood = s.selectedMood || this._data.mood_combinations?.[0] || null;
    const moodTotal  = (mood ? (mood.large_count || 0) + (mood.small_count || 0) : 0) +
                       s.extraLargeSlotCount + s.extraMiniSlotCount;
    const moodFilled = s.selectedLargePatches.length + s.selectedMinis.length +
                       s.selectedExtraLargePatches.length + s.selectedExtraMiniPatches.length;
    const addsFilled = s.selectedAdds.length + s.selectedExtraAdds.length;
    // Adds are skippable — their sectors appear only on step 3 or once something is picked
    const showAdds   = s.currentStep >= 3 || addsFilled > 0;
    const addsTotal  = showAdds ? (this._data.adds_config?.adds_count || 0) + s.extraAddsSlotCount : 0;

    const EMPTY = 'var(--color-border, #e0e0d8)';
    const segs = [s.selectedBase ? 'var(--bc-color-base, #121212)' : EMPTY];
    for (let i = 0; i < moodTotal; i++) segs.push(i < moodFilled ? 'var(--bc-color-moods, #d4ee40)' : EMPTY);
    for (let i = 0; i < addsTotal; i++) segs.push(i < addsFilled ? 'var(--bc-color-addons, #0d02de)' : EMPTY);

    const n   = segs.length;
    const arc = 360 / n;
    const gap = n > 1 ? 4 : 0;
    let angle = 0;
    const stops = [];
    segs.forEach((color) => {
      stops.push(`var(--color-background, #fff) ${angle}deg ${angle + gap}deg`);
      stops.push(`${color} ${angle + gap}deg ${angle + arc}deg`);
      angle += arc;
    });
    thumb.style.setProperty('--bc-ring', `conic-gradient(${stops.join(', ')})`);

    // Pulse when the filled count grows
    const totalFilled = (s.selectedBase ? 1 : 0) + moodFilled + addsFilled;
    if (this._ringFilled !== undefined && totalFilled > this._ringFilled) {
      thumb.classList.remove('bc-ring-pulse');
      void thumb.offsetWidth;                       // restart the animation
      thumb.classList.add('bc-ring-pulse');
      thumb.addEventListener('animationend', () => thumb.classList.remove('bc-ring-pulse'), { once: true });
      // Mobile: filling starts → NOW bring the section fully into view (step 2
      // entry deliberately skips the align to keep the base visible)
      if (window.matchMedia('(max-width: 749px)').matches) this._alignSection(true);
    }
    this._ringFilled = totalFilled;
  }

  // Slow eased scroll for the rail (duration-controlled, unlike native smooth)
  _animateRailScroll(el, target, duration) {
    if (this._railScrollRaf) cancelAnimationFrame(this._railScrollRaf);
    const from = el.scrollTop;
    const dist = target - from;
    if (Math.abs(dist) < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { el.scrollTop = target; return; }
    const start = performance.now();
    const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;  // easeInOutCubic
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      el.scrollTop = from + dist * ease(p);
      if (p < 1) this._railScrollRaf = requestAnimationFrame(tick);
      else this._railScrollRaf = null;
    };
    this._railScrollRaf = requestAnimationFrame(tick);
  }

  _openMoodboxPopup() {
    if (!this._popupEl || !this._asideEl) return;
    this._asideHome = this._asideEl.parentElement;
    this._popupEl.querySelector('.js-bc-popup-body').appendChild(this._asideEl);
    this._popupEl.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  _closeMoodboxPopup() {
    if (!this._popupEl || this._popupEl.hidden) return;
    this._popupEl.hidden = true;
    document.body.style.overflow = '';
    if (this._asideHome && this._asideEl) this._asideHome.appendChild(this._asideEl);
  }

  // ── Card selection ────────────────────────────────────────────────────────

  _onBaseCardClick(product, clickedCard) {
    // Deselect all cards
    this._panelsEl.querySelectorAll('.bundle-configurator__base-card').forEach((c) => {
      c.classList.remove('bundle-configurator__base-card--selected');
    });

    // Hide all option panels + reset their chip/label state
    this.querySelectorAll('.bundle-configurator__variant-panel').forEach((panel) => {
      panel.hidden = true;
      panel.querySelectorAll('.bundle-configurator__option-chip').forEach((chip) => {
        chip.classList.remove(
          'bundle-configurator__option-chip--selected',
          'bundle-configurator__option-chip--unavailable'
        );
        chip.disabled = false;
      });
      panel.querySelectorAll('.bundle-configurator__option-label[data-name]').forEach((label) => {
        label.textContent = label.dataset.name;
      });
    });

    clickedCard.classList.add('bundle-configurator__base-card--selected');

    this._state.selectedBase        = product;
    this._state.selectedBaseVariant = null;
    this._state.optionSelections[product.id] = {};

    const optPanel = this.querySelector(
      `.bundle-configurator__variant-panel[data-product-id="${product.id}"]`
    );

    if (optPanel) {
      // Pre-select options that match last known values for this option name
      product.options.forEach((option) => {
        const lastVal = this._state.lastOptionValues[option.name];
        if (!lastVal) return;
        if (!option.values.includes(lastVal)) return;

        this._state.optionSelections[product.id][option.position] = lastVal;

        // Find chip by value (avoid CSS.escape issues)
        const chip = Array.from(
          optPanel.querySelectorAll(`.bundle-configurator__option-chip[data-option="${option.position}"]`)
        ).find((c) => c.dataset.value === lastVal);

        if (chip) {
          chip.classList.add('bundle-configurator__option-chip--selected');
          const label = chip.closest('.bundle-configurator__option-group')
            ?.querySelector('.bundle-configurator__option-label');
          if (label) label.textContent = `${label.dataset.name}: ${lastVal}`;
        }
      });

      // For options without a preselect — take the first available chip
      product.options.forEach((option) => {
        if (this._state.optionSelections[product.id][option.position] !== undefined) return;

        const chips = Array.from(
          optPanel.querySelectorAll(`.bundle-configurator__option-chip[data-option="${option.position}"]`)
        );
        const first = chips.find((c) => !c.disabled) || chips[0];
        if (!first) return;

        first.classList.add('bundle-configurator__option-chip--selected');
        this._state.optionSelections[product.id][option.position] = first.dataset.value;

        const label = first.closest('.bundle-configurator__option-group')
          ?.querySelector('.bundle-configurator__option-label');
        if (label) label.textContent = `${label.dataset.name}: ${first.dataset.value}`;
      });

      optPanel.hidden = false;
      this._resolveVariant(product);
    } else {
      this._state.selectedBaseVariant = product.variants[0] || null;
    }

    this._renderGallery(product);
    this._renderStep1Aside();    // refresh the base shown in the "Your MoodBox" composition
    this._checkContinue();
  }

  // ── Option selection ──────────────────────────────────────────────────────

  _onOptionChipClick(product, chip, row) {
    row.querySelectorAll('.bundle-configurator__option-chip').forEach((c) => {
      c.classList.remove('bundle-configurator__option-chip--selected');
    });
    chip.classList.add('bundle-configurator__option-chip--selected');

    // Update label: "SIZE: L"
    const group = row.closest('.bundle-configurator__option-group');
    const label = group?.querySelector('.bundle-configurator__option-label');
    if (label) label.textContent = `${label.dataset.name}: ${chip.dataset.value}`;

    this._state.optionSelections[product.id][chip.dataset.option] = chip.dataset.value;

    // Remember by option name for preselect on other products
    if (label?.dataset.name) {
      this._state.lastOptionValues[label.dataset.name] = chip.dataset.value;
    }
    this._resolveVariant(product);
    this._checkContinue();
  }

  _resolveVariant(product) {
    const sel = this._state.optionSelections[product.id] || {};

    const matched = product.variants.find((v) =>
      product.options.every((opt) => v.options[opt.position - 1] === sel[opt.position])
    );
    this._state.selectedBaseVariant = matched || null;

    // Mark unavailable chips
    const optPanel = this.querySelector(
      `.bundle-configurator__variant-panel[data-product-id="${product.id}"]`
    );
    if (!optPanel) return;

    product.options.forEach((option) => {
      optPanel
        .querySelectorAll(`.bundle-configurator__option-chip[data-option="${option.position}"]`)
        .forEach((chip) => {
          const testSel  = { ...sel, [option.position]: chip.dataset.value };
          const available = product.variants.some(
            (v) =>
              product.options.every((o) => {
                const required = testSel[o.position];
                // undefined = not selected yet → do not filter
                return required === undefined || v.options[o.position - 1] === required;
              }) && v.available
          );
          chip.classList.toggle('bundle-configurator__option-chip--unavailable', !available);
          chip.disabled = !available;
        });
    });

    this._updateTotal();
  }

  // ── Continue ──────────────────────────────────────────────────────────────

  _checkContinue() {
    const ready = !!(this._state.selectedBase && this._state.selectedBaseVariant);
    const btn = this.querySelector('.js-continue-btn');
    if (btn) btn.disabled = !ready;
    const buyOnlyBtn = this.querySelector('.js-buy-only-btn');
    if (buyOnlyBtn) buyOnlyBtn.disabled = !ready;
  }

  async _onBuyOnlyThis() {
    if (!this._state.selectedBase || !this._state.selectedBaseVariant) return;
    const btn = this.querySelector('.js-buy-only-btn');
    if (btn) btn.disabled = true;
    try {
      await fetch('/cart/clear.js', { method: 'POST' });
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: this._state.selectedBaseVariant.id, quantity: 1 }] }),
      });
      if (!res.ok) throw new Error('cart error');
      window.location.href = '/checkout';
    } catch (e) {
      console.error('[BC] Buy only this error:', e);
      if (btn) btn.disabled = false;
    }
  }

  _onStep1Continue() {
    if (!this._state.selectedBase || !this._state.selectedBaseVariant) return;

    // Save base into cartItems (overwrite if already present)
    this._state.cartItems = this._state.cartItems.filter((i) => i._slot !== 'base');
    this._state.cartItems.push({
      _slot:      'base',
      id:         this._state.selectedBaseVariant.id,
      quantity:   1,
      properties: { _base_product: this._state.selectedBase.title },
    });

    this._goToStep(2);
  }

  // ── Cart ─────────────────────────────────────────────────────────────────

  // Unique bundle ID — links all line items into one group in the cart
  _generateBundleId() {
    return `moodbox-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  }

  // Payload for POST /cart/add.js
  //
  // Structure in the cart:
  //   [0] MoodBox variant     (_bundle_slot: bundle)  ← parent, price/discount managed by the app
  //   [1] Base variant        (_bundle_slot: base)    ← inventory is deducted
  //   [2] Mood patches × N    (_bundle_slot: mood)
  //   [3] Mini patches × N    (_bundle_slot: minis)
  //   [4] Add-ons × N         (_bundle_slot: adds)
  //
  // All line items are linked by a single _bundle_id.
  _buildCartPayload(bundleId) {
    const id         = bundleId || this._generateBundleId();
    const bundleItem = this._state.cartItems.find((i) => i._slot === 'bundle');
    const parentId   = bundleItem?.id;
    return {
      items: this._state.cartItems.map(({ _slot, properties = {}, ...item }) => ({
        ...item,
        ...(parentId && _slot !== 'bundle' ? { parent_id: parentId } : {}),
        properties: {
          ...properties,
          _bundle_id:   id,
          _bundle_slot: _slot,
        },
      })),
    };
  }

  // Sends the bundle to the cart in a single request
  async _addBundleToCart() {
    const bundleId = this._generateBundleId();
    const payload  = this._buildCartPayload(bundleId);

    return fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body:    JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status) throw new Error(data.description || 'Cart error');
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        return data;
      })
      .catch((err) => {
        console.error('[BC] Cart error:', err);
      });
  }

  // ── Total ─────────────────────────────────────────────────────────────────

  _updateTotal() {
    const totalEl = this._panelEl.querySelector('.js-total');
    if (!totalEl) return;
    const cents = this._state.selectedBaseVariant?.price || 0;
    totalEl.textContent = cents > 0 ? bcFormatMoney(cents, this._currency) : '';
    this._updateShippingBar();
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  _updateStepHeading(n) {
    const h = this._data.step_headings?.[String(n)];
    if (!h) return;
    const headingEl = this.querySelector('.js-step-heading');
    if (headingEl) {
      const accentClass = 'bundle-configurator__step-heading-accent';
      // Space before <br> keeps words separated on mobile, where the break is hidden
      const headingHtml = (h.heading || '').replace(/\n/g, ' <br>');
      headingEl.innerHTML = headingHtml +
        (h.accent ? ` <span class="${accentClass}">${h.accent}</span>` : '');
    }
    const descEl = this.querySelector('.js-step-description');
    if (descEl) {
      const descHtml = (h.description || '').replace(/\n/g, '<br>');
      descEl.innerHTML = descHtml;
      // Hide the whole description row (and its layout gap) when the step has no description
      const descRow = this.querySelector('.bundle-configurator__description-row');
      if (descRow) descRow.hidden = !descHtml.trim();
    }
  }

  _goToStep(n) {
    this._closeMoodboxPopup();   // return the aside home before re-rendering regions
    this._state.currentStep = n;
    this.dataset.currentStep = n;
    this._updateStepNav(n);
    this._updateStepHeading(n);
    if (n !== 3) {
      const skipSlot = this.querySelector('.js-step-skip-slot');
      if (skipSlot) skipSlot.innerHTML = '';
    }

    const step1 = this._panelsEl.querySelector('.bundle-configurator__step--1');

    // Remove any previously rendered dynamic steps
    this._panelsEl
      .querySelectorAll('.bundle-configurator__step--stub, .bundle-configurator__step--2, .bundle-configurator__step--3, .bundle-configurator__step--4')
      .forEach((el) => el.remove());

    // Cleanup shared regions from the previous dynamic step (aside / panel)
    const asideEl = this._asideEl;
    if (asideEl) asideEl.innerHTML = '';
    this._panelEl.querySelector('.js-primary-dynamic')?.remove();
    const mobExtraEl = this._panelEl.querySelector('.js-panel-extra-mobile');
    if (mobExtraEl) mobExtraEl.innerHTML = '';

    if (n === 1) {
      // Reset shared panel elements for step 1
      const back = this._panelEl.querySelector('.js-bundle-back');
      if (back) { back.hidden = true; back.onclick = null; }
      const retailEl = this._panelEl.querySelector('.js-retail-total');
      if (retailEl) { retailEl.textContent = ''; retailEl.hidden = true; }
      const savingsEl = this._panelEl.querySelector('.js-savings');
      if (savingsEl) { savingsEl.hidden = true; savingsEl.innerHTML = ''; }

      if (step1) step1.hidden = false;
      const stepLabel = this.querySelector('.js-step-label');
      if (stepLabel) stepLabel.textContent = this._t.choose_your_base || '';
      if (this._state.selectedBase) this._renderGallery(this._state.selectedBase);
      this._updateTotal();
      this._renderStep1Aside();
    } else {
      if (n === 2 || n === 3) { this._showStepPlaceholder(n); }
      else { this._restoreMedia(); }
      if (step1) step1.hidden = true;
      if (n === 2) {
        this._renderStep2().catch((e) => console.error('[BC] Step 2 render error:', e));
      } else if (n === 3) {
        this._renderStep3().catch((e) => console.error('[BC] Step 3 render error:', e));
      } else if (n === 4) {
        this._renderStep4();
      } else {
        this._renderStub(n);
      }
    }

    // One-screen layout: the right column scrolls internally — reset it for the new step
    if (this._panelsEl) this._panelsEl.scrollTop = 0;

    // Align the section itself in the viewport (both directions: a step change
    // should restore alignment even if the visitor scrolled away).
    // Mobile exception: entering step 2 keeps the page as is so the base stays
    // visible — the first fill aligns instead (see _updateRailRing).
    const skipAlign = n === 2 && window.matchMedia('(max-width: 749px)').matches;
    if (!skipAlign) {
      requestAnimationFrame(() => requestAnimationFrame(() => this._alignSection(true)));
    }
  }

  _updateStepNav(activeStep) {
    this.querySelectorAll('.bundle-configurator__step-dot').forEach((dot) => {
      const step = parseInt(dot.dataset.step, 10);
      dot.classList.toggle('bundle-configurator__step-dot--active',    step === activeStep);
      dot.classList.toggle('bundle-configurator__step-dot--completed', step < activeStep);
    });
  }

  _updateMedia(src, alt) {
    const img = this._mediaEl.querySelector('.bundle-configurator__media-img');
    if (!img || !src) return;
    img.src = src;
    if (alt) img.alt = alt;
  }

  _renderGallery(product) {
    this._setInfoProduct(product);
    // Same item already in the gallery → skip the rebuild (no flicker on select/remove)
    const key = String(product.groupId || product.id || '');
    if (key && key === this._galleryKey && this._mediaEl.firstElementChild) return;
    this._galleryKey = key;

    // Soft swap: fade the old gallery out, rebuild, fade the new one in.
    // Preload the incoming first image during the fade so it paints instantly.
    const media  = this._mediaEl;
    const swapId = (this._gallerySwapId = (this._gallerySwapId || 0) + 1);
    const first  = product.media?.find((m) => m.type !== 'video');
    const preSrc = first?.src || product.featured_image;
    if (preSrc) { const pre = new Image(); pre.src = preSrc; }

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!media.firstElementChild || reduce) {
      this._buildGallery(product);
      return;
    }
    media.classList.add('bc-media-swap');
    setTimeout(() => {
      if (this._gallerySwapId !== swapId) return;   // superseded by a newer click
      this._buildGallery(product);
      requestAnimationFrame(() => requestAnimationFrame(() => media.classList.remove('bc-media-swap')));
    }, 160);
  }

  _buildGallery(product) {
    this._galleryRO?.disconnect();       this._galleryRO = null;
    this._galleryThumbMO?.disconnect();  this._galleryThumbMO = null;
    this._galleryVideoIO?.disconnect();  this._galleryVideoIO = null;

    const media = (product.media || []).filter((m) => m.type !== 'model_3d');
    if (!media.length) {
      this._mediaEl.innerHTML = `<img class="bundle-configurator__media-img" alt="" width="${this._imgSize}" height="${this._imgHeight}">`;
      this._updateMedia(product.featured_image, product.title);
      return;
    }

    this._mediaEl.innerHTML = '';

    const ARROW_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>';

    // Root: theme's slideshow-component custom element
    const slideshowEl = document.createElement('slideshow-component');
    slideshowEl.className = 'bundle-configurator__gallery';
    slideshowEl.setAttribute('initial-slide', '0');
    if (media.length > 1) slideshowEl.setAttribute('infinite', '');

    // Left thumbnails column (inside slideshow-component so on:click refs resolve)
    const thumbsEl = document.createElement('div');
    thumbsEl.className = 'bundle-configurator__gallery-thumbs';

    // Slideshow container
    const containerEl = document.createElement('slideshow-container');
    containerEl.setAttribute('ref', 'slideshowContainer');
    containerEl.className = 'bundle-configurator__gallery-main-wrap';

    // Arrows
    if (media.length > 1) {
      const arrowsEl = document.createElement('slideshow-arrows');
      arrowsEl.setAttribute('position', 'center');

      const mkArrow = (action) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('on:click', `/${action}`);
        btn.setAttribute('ref', action);
        btn.setAttribute('aria-label', action === 'previous'
          ? (this._t.slide_prev || 'Previous')
          : (this._t.slide_next || 'Next'));
        btn.className = [
          'slideshow-control',
          `slideshow-control--${action}`,
          'slideshow-control--style-arrow',
          'button button-unstyled button-unstyled--transparent',
          'bundle-configurator__gallery-arrow',
          action === 'previous' ? 'flip-x' : '',
        ].join(' ');
        btn.innerHTML = `<span class="svg-wrapper icon-arrow">${ARROW_SVG}</span>`;
        return btn;
      };

      arrowsEl.append(mkArrow('previous'), mkArrow('next'));
      containerEl.appendChild(arrowsEl);
    }

    // Slides scroller
    const slidesEl = document.createElement('slideshow-slides');
    slidesEl.setAttribute('ref', 'scroller');
    slidesEl.className = 'bundle-configurator__gallery-slides';

    media.forEach((item, idx) => {
      // Thumbnail
      const thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'bundle-configurator__gallery-thumb';
      thumb.setAttribute('on:click', `/select/${idx}`);
      thumb.setAttribute('ref', 'thumbnails[]');
      if (idx === 0) thumb.setAttribute('aria-selected', 'true');

      if (item.type === 'video') {
        const badge = document.createElement('span');
        badge.className = 'bundle-configurator__gallery-thumb-play';
        badge.textContent = '▶';
        thumb.appendChild(badge);
      }

      const tImg = document.createElement('img');
      tImg.src    = item.thumb;
      tImg.alt    = item.alt || '';
      tImg.width  = 80;
      tImg.height = 80;
      thumb.appendChild(tImg);
      thumbsEl.appendChild(thumb);

      // Slide
      const slide = document.createElement('slideshow-slide');
      slide.setAttribute('ref', 'slides[]');
      slide.setAttribute('slide-id', `bc-slide-${idx}`);
      slide.className = 'bundle-configurator__gallery-slide';

      if (item.type === 'video') {
        const video = document.createElement('video');
        video.src         = item.src;
        video.poster      = item.thumb;
        video.className   = 'bundle-configurator__gallery-slide-media';
        video.loop        = true;
        video.muted       = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        slide._bcVideo = video;
        slide.appendChild(video);
      } else {
        const img = document.createElement('img');
        img.src       = item.src;
        img.alt       = item.alt || '';
        img.className = 'bundle-configurator__gallery-slide-media';
        img.width     = this._imgSize;
        img.height    = this._imgHeight;
        slide.appendChild(img);
      }

      slidesEl.appendChild(slide);
    });

    containerEl.appendChild(slidesEl);
    slideshowEl.append(thumbsEl, containerEl);
    this._mediaEl.appendChild(slideshowEl);

    // Track "no further slide" — CSS hides the always-visible next arrow at the end
    const updateAtEnd = () => {
      const atEnd = slidesEl.scrollLeft + slidesEl.clientWidth >= slidesEl.scrollWidth - 2;
      slideshowEl.classList.toggle('bc-gallery-at-end', atEnd);
    };
    slidesEl.addEventListener('scroll', updateAtEnd, { passive: true });
    requestAnimationFrame(updateAtEnd);

    // Autoplay video slides when they scroll into view
    const videoSlides = [...slidesEl.querySelectorAll('slideshow-slide')].filter(s => s._bcVideo);
    if (videoSlides.length) {
      this._galleryVideoIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target._bcVideo;
          if (entry.isIntersecting) { v.play().catch(() => {}); }
          else { v.pause(); v.currentTime = 0; }
        });
      }, { root: slidesEl, threshold: 0.5 });
      videoSlides.forEach(s => this._galleryVideoIO.observe(s));
    }

    // Capture wheel events so hovering over thumbs scrolls the column, not the page
    thumbsEl.addEventListener('wheel', (e) => {
      const atTop    = thumbsEl.scrollTop === 0 && e.deltaY < 0;
      const atBottom = thumbsEl.scrollTop + thumbsEl.clientHeight >= thumbsEl.scrollHeight && e.deltaY > 0;
      if (!atTop && !atBottom) {
        e.preventDefault();
        thumbsEl.scrollTop += e.deltaY;
      }
    }, { passive: false });

    // Bottom fade of the thumbs is a scroll hint — drop it at the last thumb
    // (or when there is nothing to scroll), restore when scrolling back up
    const updateThumbsFade = () => {
      const atEnd = thumbsEl.scrollTop + thumbsEl.clientHeight >= thumbsEl.scrollHeight - 2;
      thumbsEl.classList.toggle('bc-thumbs-at-end', atEnd);
    };
    thumbsEl.addEventListener('scroll', updateThumbsFade, { passive: true });

    // Keep thumbs height = main image height regardless of thumb count
    const syncThumbsHeight = () => {
      const h = containerEl.getBoundingClientRect().height;
      if (h > 0) thumbsEl.style.maxHeight = h + 'px';
      updateThumbsFade();                 // height changed → the at-end state may too
    };
    this._galleryRO = new ResizeObserver(syncThumbsHeight);
    this._galleryRO.observe(containerEl);
    requestAnimationFrame(syncThumbsHeight);

    // Scroll active thumbnail into view when slide changes
    this._galleryThumbMO = new MutationObserver(() => {
      const active = thumbsEl.querySelector('.bundle-configurator__gallery-thumb[aria-selected="true"]');
      if (!active) return;
      const thumbTop    = active.offsetTop;
      const thumbBottom = thumbTop + active.offsetHeight;
      const scrollTop   = thumbsEl.scrollTop;
      const visible     = thumbsEl.clientHeight;
      if (thumbTop < scrollTop) {
        thumbsEl.scrollTo({ top: thumbTop, behavior: 'smooth' });
      } else if (thumbBottom > scrollTop + visible) {
        thumbsEl.scrollTo({ top: thumbBottom - visible, behavior: 'smooth' });
      }
    });
    this._galleryThumbMO.observe(thumbsEl, { subtree: true, attributeFilter: ['aria-selected'] });
  }

  _restoreMedia() {
    this._galleryKey = null;             // media replaced → next _renderGallery must rebuild
    if (!this._mediaEl.querySelector('.bundle-configurator__gallery')) return;
    this._galleryRO?.disconnect();
    this._galleryRO = null;
    this._galleryThumbMO?.disconnect();
    this._galleryThumbMO = null;
    this._galleryVideoIO?.disconnect();
    this._galleryVideoIO = null;
    this._mediaEl.innerHTML = `<img class="bundle-configurator__media-img" alt="" width="${this._imgSize}" height="${this._imgHeight}">`;
  }

  _showStepPlaceholder(step) {
    this._setInfoProduct(null);          // placeholder → no product info in the gallery
    this._galleryKey = null;             // media replaced → next _renderGallery must rebuild
    this._galleryRO?.disconnect();       this._galleryRO = null;
    this._galleryThumbMO?.disconnect();  this._galleryThumbMO = null;
    this._galleryVideoIO?.disconnect();  this._galleryVideoIO = null;
    const src = this._data[`placeholder_step${step}`];
    if (src) {
      this._mediaEl.innerHTML = `<img class="bundle-configurator__media-img" alt="" width="${this._imgSize}" height="${this._imgHeight}">`;
      this._updateMedia(src, '');
    } else {
      this._mediaEl.innerHTML = `<div class="bundle-configurator__media-placeholder"></div>`;
    }
  }

  // Applies step button colors from section settings (data island step_buttons)
  _styleStepBtn(btn, cfg) {
    if (!btn || !cfg) return;
    if (cfg.bg)    btn.style.setProperty('--bc-btn-bg', cfg.bg);
    if (cfg.color) btn.style.setProperty('--bc-btn-color', cfg.color);
  }

  // Shared back button in the panel — show, label, wire the transition.
  // before — optional callback before the transition (e.g. unlock the shipping bar).
  _wireSharedBack(toStep, before) {
    const back = this._panelEl.querySelector('.js-bundle-back');
    if (!back) return;
    back.hidden = false;
    back.textContent = '← ' + (this._t.back || 'Back');
    back.onclick = () => { before?.(); this._goToStep(toStep); };
  }

  // Moves step blocks from the right column into the shared regions:
  // slots+extra → aside (left column), primary → panel (.js-panel-primary),
  // plus a mobile duplicate of extra-btns in .js-panel-extra-mobile.
  _relocateToRegions(stepEl, opts) {
    const aside = this._asideEl;
    if (aside) {
      aside.innerHTML = '';
      const extraEl = opts.extra ? stepEl.querySelector(opts.extra) : null;
      const slotsEl = opts.slots ? stepEl.querySelector(opts.slots) : null;
      // Aside header: "Your MoodBox" title (left) + extra buttons (right). Desktop, steps 2-3.
      if (slotsEl || extraEl) {
        const head = document.createElement('div');
        head.className = 'bc-aside__header';
        const title = document.createElement('h3');
        title.className = 'bc-aside__title';
        title.textContent = this._t.your_moodbox || 'Your MoodBox';
        head.appendChild(title);
        if (extraEl) head.appendChild(extraEl);
        aside.appendChild(head);
      }
      if (slotsEl) aside.appendChild(slotsEl);
    }

    if (opts.primary) {
      const btn     = stepEl.querySelector(opts.primary);
      const primary = this._panelEl.querySelector('.js-panel-primary');
      if (btn && primary) {
        let dyn = primary.querySelector('.js-primary-dynamic');
        if (!dyn) { dyn = document.createElement('div'); dyn.className = 'js-primary-dynamic'; primary.appendChild(dyn); }
        dyn.innerHTML = '';
        dyn.appendChild(btn);
      }
    }

    const mob = this._panelEl.querySelector('.js-panel-extra-mobile');
    if (mob) {
      mob.innerHTML = '';
      if (opts.extraBtns?.length) {
        const wrap = document.createElement('div');
        wrap.className = 'bundle-configurator__extra-btns';
        // Selected counter (mobile) — first item, updated as slots fill
        const count = document.createElement('span');
        count.className = 'bundle-configurator__selected-count js-selected-count';
        wrap.appendChild(count);
        opts.extraBtns.forEach(([cls, fn]) => {
          const src = this._asideEl.querySelector(cls); // original in the aside (text/classes/disabled)
          if (!src) return;
          const b = src.cloneNode(true);
          b.disabled = src.disabled;
          b.addEventListener('click', fn);
          wrap.appendChild(b);
        });
        mob.appendChild(wrap);
      }
    }
    this._updateSelectedCount();
  }

  // Updates the mobile "X/Y Selected" counter for steps 2 (moods) and 3 (adds).
  _updateSelectedCount() {
    const countEl = this._panelEl?.querySelector('.js-selected-count');
    if (!countEl) return;
    const s = this._state;
    const step = s.currentStep;
    let filled = 0, total = 0;
    if (step === 2 && s.selectedMood) {
      // Extra slots grow the total too (e.g. 4 base + 1 extra → x/5)
      total  = (s.selectedMood.large_count || 0) + (s.selectedMood.small_count || 0) +
               s.extraLargeSlotCount + s.extraMiniSlotCount;
      filled = s.selectedLargePatches.length + s.selectedMinis.length +
               s.selectedExtraLargePatches.length + s.selectedExtraMiniPatches.length;
    } else if (step === 3 && this._data.adds_config) {
      total  = (this._data.adds_config.adds_count || 0) + s.extraAddsSlotCount;
      filled = s.selectedAdds.length + s.selectedExtraAdds.length;
    } else {
      countEl.textContent = '';
      return;
    }
    countEl.textContent = `${filled}/${total} ${this._data.selected_label || 'Selected'}`;
  }

  // ── Stubs for steps 2–4 ───────────────────────────────────────────────────

  _renderStub(n) {
    const tmpl = document.getElementById('tmpl-bc-stub');
    if (!tmpl) return;
    const frag = document.importNode(tmpl.content, true);
    const step = frag.querySelector('.bundle-configurator__step--stub');

    const back = step.querySelector('.bundle-configurator__back-btn');
    back.textContent = '← ' + (this._t.back || 'Back');
    back.addEventListener('click', () => {
      this._goToStep(n - 1);
    });

    const stepLabels = { 4: this._t.check_your_box || 'Check your box' };
    const stepLabel  = this.querySelector('.js-step-label');
    if (stepLabel) stepLabel.textContent = stepLabels[n] || '';

    const labels = {
      2: this._t.stub_step2 || 'Choose your mood',
      3: this._t.stub_step3 || 'Choose your patches',
      4: this._t.stub_step4 || 'Add-ons',
    };
    step.querySelector('.bundle-configurator__stub').textContent =
      `${this._t.step} ${n} — ${labels[n] || ''}`;

    this._panelsEl.appendChild(frag);
  }

  // ── Step 2 render ─────────────────────────────────────────────────────────

  async _renderStep2() {
    const tmpl = document.getElementById('tmpl-bc-step2');
    if (!tmpl) return;
    const frag   = document.importNode(tmpl.content, true);
    const stepEl = frag.querySelector('.bundle-configurator__step--2');

    this._wireSharedBack(1);

    const continueBtn = stepEl.querySelector('.js-step2-continue');
    const btn2Cfg = this._data.step_buttons?.['2'] || {};
    continueBtn.innerHTML = `${btn2Cfg.text || this._t.continue || 'Continue'} <span class="svg-wrapper">${_BC_ARROW_SVG}</span>`;
    this._styleStepBtn(continueBtn, btn2Cfg);
    continueBtn.addEventListener('click', () => this._onStep2Continue());

    const extraLargeBtn = stepEl.querySelector('.js-add-extra-large');
    const extraMiniBtn  = stepEl.querySelector('.js-add-extra-mini');
    if (extraLargeBtn) extraLargeBtn.addEventListener('click', () => this._addExtraLargeSlot(stepEl));
    if (extraMiniBtn)  extraMiniBtn.addEventListener('click',  () => this._addExtraMiniSlot(stepEl));

    const tabsEl    = stepEl.querySelector('.js-mood-tabs');
    const prevHandle = this._state.selectedMood?.handle;
    this._data.mood_combinations.forEach((mood, i) => {
      const tabFrag = document.importNode(document.getElementById('tmpl-bc-mood-tab').content, true);
      const tab     = tabFrag.querySelector('.bundle-configurator__mood-tab');
      tab.dataset.handle = mood.handle;
      const lbl = document.createElement('span');
      lbl.className   = 'bundle-configurator__mood-tab-label';
      // Wrap "MOOD"/"MOODS" so it can be hidden on mobile (short one-line labels)
      lbl.innerHTML = String(mood.label)
        .replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
        .replace(/\bMOODS?\b/gi, '<span class="bc-mood-word">$&</span>');
      const prc = document.createElement('span');
      prc.className   = 'bundle-configurator__mood-tab-price';
      tab.append(lbl, prc);
      const isActive = prevHandle ? mood.handle === prevHandle : i === 0;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) tab.classList.add('bundle-configurator__mood-tab--active');
      tab.addEventListener('click', () => this._selectMoodTab(mood, stepEl));
      tabsEl.appendChild(tabFrag);
    });

    this._panelsEl.appendChild(frag);

    // Distribute movable blocks: slots+extra-btns → aside, continue → panel, extra duplicate → mobile
    this._relocateToRegions(stepEl, {
      slots:   '.js-step2-slots-row',
      extra:   '.js-step2-extra',
      primary: '.js-step2-primary',
      extraBtns: [
        ['.js-add-extra-large', () => this._addExtraLargeSlot(stepEl)],
        ['.js-add-extra-mini',  () => this._addExtraMiniSlot(stepEl)],
      ],
    });

    // Already-picked adds (step 3) shown after the patch slots, removable
    this._renderStep2AsideAdds();

    if (this._data.mood_combinations.length > 0) {
      const moodToRestore = this._data.mood_combinations.find(
        (m) => m.handle === prevHandle
      ) || this._data.mood_combinations[0];
      await this._selectMoodTab(moodToRestore, stepEl);
    }

    // Prefetch remaining moods' collections and populate all tab prices
    this._prefetchAllMoodPrices(stepEl);
  }

  // ── Step 2 mood tab ───────────────────────────────────────────────────────

  async _selectMoodTab(mood, stepEl) {
    stepEl.querySelectorAll('.bundle-configurator__mood-tab').forEach((tab) => {
      const active = tab.dataset.handle === mood.handle;
      tab.classList.toggle('bundle-configurator__mood-tab--active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    const isSameMood = this._state.selectedMood?.handle === mood.handle;
    this._state.selectedMood = mood;
    if (!isSameMood) {
      this._state.selectedLargePatches      = [];
      this._state.selectedExtraLargePatches = [];
      this._state.extraLargeSlotCount       = 0;
      this._state.selectedMinis             = [];
      this._state.selectedExtraMiniPatches  = [];
      this._state.extraMiniSlotCount        = 0;
    }

    const stepLabel = this.querySelector('.js-step-label');
    if (stepLabel) stepLabel.textContent = this._t.choose_your_moods || 'Choose your moods';

    this._renderPatchSlots(mood, stepEl);
    this._checkStep2Continue(stepEl);
    this._updateStep2Total(stepEl);

    // Show skeletons while fetching
    if (mood.large_collections.length > 0) {
      const sec = stepEl.querySelector('.js-large-section');
      if (sec) sec.hidden = false;
      this._showGridSkeleton(stepEl.querySelector('.js-large-grid'));
    }
    if (mood.small_collections.length > 0) {
      this._showGridSkeleton(stepEl.querySelector('.js-small-grid'));
    }

    const allHandles = [...new Set([...mood.large_collections, ...mood.small_collections])];
    try {
      await this._fetchPatches(allHandles);
    } catch (e) {
      console.error('[BC] Fetch patches failed:', e);
      return;
    }

    if (mood.large_collections.length > 0) {
      const largeProds = this._dedupeProducts(mood.large_collections.flatMap((h) => this._state.patchesCache[h] || []));
      stepEl.querySelector('.js-large-label').textContent =
        this._t.choose_maxi_moods || 'Choose maxi moods';
      this._renderTagFilters(largeProds, stepEl.querySelector('.js-large-filters'), () => this._refreshLargeGrid(stepEl));
      this._renderPatchCards(largeProds, stepEl.querySelector('.js-large-grid'), true, stepEl);
      this._updateLargeMaxState(stepEl);
    }

    if (mood.small_collections.length > 0) {
      const smallProds = this._dedupeProducts(mood.small_collections.flatMap((h) => this._state.patchesCache[h] || []));
      stepEl.querySelector('.js-small-label').textContent =
        this._t.choose_mini_moods || 'Choose mini moods';
      this._renderTagFilters(smallProds, stepEl.querySelector('.js-small-filters'), () => this._refreshSmallGrid(stepEl));
      this._renderPatchCards(smallProds, stepEl.querySelector('.js-small-grid'), false, stepEl);
    }

    this._refreshSlots(stepEl);
    this._renderExtraSlots(stepEl);
    this._updateExtraBtnStates(stepEl);
    this._updateLargeMaxState(stepEl);
    this._updateMiniMaxState(stepEl);
    this._checkStep2Continue(stepEl);
    this._updateSectionVisibility(stepEl);
  }

  // ── Mood price calculation ────────────────────────────────────────────────

  _calcMoodPrice(mood) {
    const BUNDLE = 'Bundle';
    const retailVal = this._bcRetailVal;

    // Returns { bundle, retail } prices (cents) from the first product in given collections
    const findPrices = (collections) => {
      for (const handle of collections) {
        for (const product of (this._state.patchesCache[handle] || [])) {
          const bcOpt = product.options?.find((o) => o.name === '_bc_price');
          if (bcOpt) {
            const key      = `option${bcOpt.position}`;
            const bundleV  = product.variants.find((v) => v[key] === BUNDLE && v.available)
                          || product.variants.find((v) => v[key] === BUNDLE);
            const retailV  = product.variants.find((v) => v[key] === retailVal && v.available)
                          || product.variants.find((v) => v[key] === retailVal);
            if (bundleV) return { bundle: bundleV.price, retail: retailV?.price ?? bundleV.price };
          } else {
            const v = product.variants.find((v) => v.available) || product.variants[0];
            if (v) return { bundle: v.price, retail: v.price };
          }
        }
      }
      return { bundle: 0, retail: 0 };
    };

    let bundleCents = 0;
    let retailCents = 0;

    if (mood.large_count > 0) {
      const p = findPrices(mood.large_collections);
      bundleCents += p.bundle * mood.large_count;
      retailCents += p.retail * mood.large_count;
    }
    if (mood.small_count > 0) {
      const p = findPrices(mood.small_collections);
      bundleCents += p.bundle * mood.small_count;
      retailCents += p.retail * mood.small_count;
    }

    return { bundlePrice: bundleCents, retailPrice: retailCents };
  }

  async _prefetchAllMoodPrices(stepEl) {
    const allHandles = [...new Set(
      this._data.mood_combinations.flatMap((m) => [...m.large_collections, ...m.small_collections])
    )];
    try {
      await this._fetchPatches(allHandles);
    } catch (e) {
      console.error('[BC] Prefetch mood prices failed:', e);
      return;
    }
    this._data.mood_combinations.forEach((mood) => {
      const { bundlePrice, retailPrice } = this._calcMoodPrice(mood);
      if (bundlePrice <= 0) return;
      mood.price       = bundlePrice;
      mood.retailPrice = retailPrice;
      const tab     = stepEl.querySelector(`.bundle-configurator__mood-tab[data-handle="${mood.handle}"]`);
      const priceEl = tab?.querySelector('.bundle-configurator__mood-tab-price');
      if (!priceEl) return;
      const savings = retailPrice - bundlePrice;
      const pct     = savings > 0 ? Math.round(savings / retailPrice * 100) : 0;
      priceEl.innerHTML =
        `<span class="bc-tab-price-bundle">${bcFormatMoney(bundlePrice, this._currency)}</span>` +
        (savings > 0
          ? `<del class="bc-tab-price-retail">${bcFormatMoney(retailPrice, this._currency)}</del>` +
            `<span class="bc-pct-badge">-${pct}%</span>`
          : '');
    });
    // Refresh total in case the active mood's price changed
    this._updateStep2Total(stepEl);
  }

  // ── Storefront API ────────────────────────────────────────────────────────

  async _fetchPatches(handles) {
    const toFetch = handles.filter(
      (h) => !Object.prototype.hasOwnProperty.call(this._state.patchesCache, h)
    );
    if (!toFetch.length) return;

    await Promise.all(
      toFetch.map(async (handle) => {
        try {
          const res  = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250`);
          const json = await res.json();

          this._state.patchesCache[handle] = (json.products || []).map((p) => {
            // featured_image may be a string, { src } object, or null; images[] is the reliable fallback
            const toUrl = (v) => !v ? null : (typeof v === 'string' ? v : (v.src || null));
            const productImg = toUrl(p.featured_image) || toUrl(p.images?.[0]) || null;

            return {
              id:             p.id,
              title:          p.title,
              handle:         p.handle,
              description:    p.body_html || '',
              tags:           p.tags || [],
              featured_image: bcCdnResize(productImg, 600), // 2x of 300px card
              media:          (p.images || []).map((img) => ({
                type:  'image',
                thumb: bcCdnResize(img.src, 320),           // 2x of 160px thumb
                src:   bcCdnResize(img.src, this._imgSize2x),
                alt:   img.alt || '',
              })),
              price:          p.price_min, // already in cents
              options:        p.options || [],
              variants:       (p.variants || []).map((v) => ({
                id:        v.id,
                title:     v.title,
                price:     Math.round(parseFloat(v.price) * 100), // API returns dollars string
                available: v.available,
                image:     bcCdnResize(v.featured_image?.src || null, 600), // 2x of 300px
                option1:   v.option1 || null,
                option2:   v.option2 || null,
                option3:   v.option3 || null,
              })),
            };
          });
        } catch (e) {
          console.error(`[BC] Failed to fetch collection "${handle}":`, e);
          this._state.patchesCache[handle] = [];
        }
      })
    );
  }

  // ── Patch slots ───────────────────────────────────────────────────────────

  _makeBaseSlot() {
    const tmpl      = document.getElementById('tmpl-bc-patch-slot');
    const frag      = document.importNode(tmpl.content, true);
    const slot      = frag.querySelector('.bundle-configurator__patch-slot');
    slot.dataset.slotGroup = 'base';
    slot.dataset.slotType  = 'base';
    const removeBtn = slot.querySelector('.bundle-configurator__patch-slot-remove');
    if (removeBtn) removeBtn.remove();
    const base = this._state.selectedBase;
    if (base) {
      this._fillSlot(slot, { image: base.featured_image, displayTitle: base.title });
      slot.addEventListener('click', () => { this._renderGallery(base); this._scrollToMedia(); });
    }
    return frag;
  }

  // ── Cross-step bundle composition (aside) ─────────────────────────────────

  // Removes a step-2 item (mood patch) from whichever state array holds it
  _removeMoodItem(item) {
    for (const key of ['selectedLargePatches', 'selectedMinis', 'selectedExtraLargePatches', 'selectedExtraMiniPatches']) {
      const i = this._state[key].indexOf(item);
      if (i !== -1) { this._state[key].splice(i, 1); return true; }
    }
    return false;
  }

  // Removes a step-3 item (add) from whichever state array holds it
  _removeAddItem(item) {
    for (const key of ['selectedAdds', 'selectedExtraAdds']) {
      const i = this._state[key].indexOf(item);
      if (i !== -1) { this._state[key].splice(i, 1); return true; }
    }
    return false;
  }

  _makeReadonlySep() {
    const sep = document.createElement('span');
    sep.className = 'bundle-configurator__patch-slot-sep bundle-configurator__patch-slot-sep--base';
    return sep;
  }

  // Renders the bundle composition as readonly slots with remove buttons.
  // Large patches keep the large slot size. Removing an item navigates to the
  // step it belongs to (2 for moods, 3 for adds) so the user can re-pick there;
  // that step re-renders everything from state. opts.removable: false hides the
  // remove buttons entirely (view-only mode, e.g. step 4 review).
  _renderReadonlyComposition(container, opts = {}) {
    container.innerHTML = '';
    if (opts.base) container.appendChild(this._makeBaseSlot());

    const removable = opts.removable !== false;
    const tmpl = document.getElementById('tmpl-bc-patch-slot');
    const addSlot = (item, group, type, onRemove) => {
      const frag = document.importNode(tmpl.content, true);
      const slot = frag.querySelector('.bundle-configurator__patch-slot');
      slot.dataset.slotGroup = group;
      slot.dataset.slotType  = type;
      this._fillSlot(slot, item);
      slot.addEventListener('click', () => { if (item.image) { this._renderGallery(item); this._scrollToMedia(); } });
      const removeBtn = slot.querySelector('.bundle-configurator__patch-slot-remove');
      if (removeBtn) {
        if (removable) removeBtn.addEventListener('click', (e) => { e.stopPropagation(); onRemove(); });
        else removeBtn.remove();
      }
      container.appendChild(frag);
    };

    if (opts.moods) {
      const groups = [
        [this._state.selectedLargePatches,      'large'],
        [this._state.selectedMinis,             'small'],
        [this._state.selectedExtraLargePatches, 'large'],
        [this._state.selectedExtraMiniPatches,  'small'],
      ];
      if (groups.some(([arr]) => arr.length)) {
        container.appendChild(this._makeReadonlySep());
        groups.forEach(([arr, type]) => [...arr].forEach((item) =>
          addSlot(item, 'mood', type, () => { this._removeMoodItem(item); this._goToStep(2); })
        ));
      }
    }

    if (opts.adds) {
      const adds = [...this._state.selectedAdds, ...this._state.selectedExtraAdds];
      if (adds.length) {
        container.appendChild(this._makeReadonlySep());
        adds.forEach((item) =>
          addSlot(item, 'add', 'add-readonly', () => { this._removeAddItem(item); this._goToStep(3); })
        );
      }
    }
  }

  // Builds a full composition view (title + base + moods + adds) into the aside
  _renderCompositionAside(removable) {
    const aside = this._asideEl;
    if (!aside) return;
    aside.innerHTML = '';        // idempotent: safe to re-render (e.g. base change on step 1)

    const head = document.createElement('div');
    head.className = 'bc-aside__header';
    const title = document.createElement('h3');
    title.className = 'bc-aside__title';
    title.textContent = this._t.your_moodbox || 'Your MoodBox';
    head.appendChild(title);
    aside.appendChild(head);

    const row = document.createElement('div');
    row.className = 'bundle-configurator__footer-slots-row';
    const slots = document.createElement('div');
    slots.className = 'bundle-configurator__footer-slots';
    const comp = document.createElement('div');
    comp.className = 'bundle-configurator__step3-readonly-slots';
    slots.appendChild(comp);
    row.appendChild(slots);
    aside.appendChild(row);

    this._renderReadonlyComposition(comp, { base: true, moods: true, adds: true, removable });
  }

  // Step 1: once the bundle has step 2/3 items, show the full composition under the gallery
  _renderStep1Aside() {
    const s = this._state;
    const hasContent = s.selectedLargePatches.length || s.selectedMinis.length ||
      s.selectedExtraLargePatches.length || s.selectedExtraMiniPatches.length ||
      s.selectedAdds.length || s.selectedExtraAdds.length;
    if (!hasContent) { if (this._asideEl) this._asideEl.innerHTML = ''; return; }  // empty aside stays hidden via :empty
    this._renderCompositionAside(true);
  }

  // Step 2: show already-picked adds (step 3) after the interactive patch slots
  _renderStep2AsideAdds() {
    const slotsWrap = this._asideEl.querySelector('.bundle-configurator__footer-slots');
    if (!slotsWrap) return;
    let box = slotsWrap.querySelector('.js-aside-adds-readonly');
    if (!box) {
      box = document.createElement('div');
      box.className = 'bundle-configurator__step3-readonly-slots js-aside-adds-readonly';
      slotsWrap.appendChild(box);
    }
    this._renderReadonlyComposition(box, { adds: true });
  }

  _renderStep3ReadonlySlots(stepEl) {
    const container = this._asideEl.querySelector('.js-step3-readonly-slots');
    if (!container) return;
    this._renderReadonlyComposition(container, { base: true, moods: true });
    // Separator between the readonly group and the interactive adds slots
    container.appendChild(this._makeReadonlySep());
  }


  // The gallery sits at the top of the section on both breakpoints (left column on
  // desktop, first row on mobile), so showing it = aligning the section. Using the
  // SAME target as step navigation means no pixel drift when clicking slots.
  _scrollToMedia() {
    requestAnimationFrame(() => this._alignSection(true));
  }

  _renderPatchSlots(mood, stepEl) {
    const slotsEl  = this._asideEl.querySelector('.js-patch-slots');
    slotsEl.innerHTML = '';

    // Base product slot (readonly)
    slotsEl.appendChild(this._makeBaseSlot());
    const baseSep = document.createElement('span');
    baseSep.className   = 'bundle-configurator__patch-slot-sep bundle-configurator__patch-slot-sep--base';
    baseSep.textContent = '|';
    slotsEl.appendChild(baseSep);

    const slotTmpl = document.getElementById('tmpl-bc-patch-slot');
    for (let i = 0, total = mood.large_count + mood.small_count; i < total; i++) {
      // Insert "+" separator between large and small groups
      if (i === mood.large_count && mood.large_count > 0 && mood.small_count > 0) {
        const sep = document.createElement('span');
        sep.className   = 'bundle-configurator__patch-slot-sep';
        sep.textContent = '+';
        slotsEl.appendChild(sep);
      }

      const frag      = document.importNode(slotTmpl.content, true);
      const slot      = frag.querySelector('.bundle-configurator__patch-slot');
      const removeBtn = slot.querySelector('.bundle-configurator__patch-slot-remove');
      slot.dataset.slotIdx   = i;
      slot.dataset.slotType  = i < mood.large_count ? 'large' : 'small';
      slot.dataset.slotGroup = 'mood';

      slot.addEventListener('click', (e) => {
        if (e.target.closest('.bundle-configurator__patch-slot-remove')) return;
        if (slot._bcProduct) { this._renderGallery(slot._bcProduct); this._scrollToMedia(); }
      });

      if (i < mood.large_count) {
        const largeIdx = i;
        removeBtn.addEventListener('click', () => {
          if (!this._state.selectedLargePatches[largeIdx]) return;
          this._state.selectedLargePatches.splice(largeIdx, 1);
          const selectedGroupIds = new Set([
            ...this._state.selectedLargePatches.map((p) => p.groupId || String(p.id)),
            ...this._state.selectedExtraLargePatches.map((p) => p.groupId || String(p.id)),
          ]);
          const extraGroupIds = new Set(this._state.selectedExtraLargePatches.map((p) => p.groupId || String(p.id)));
          stepEl.querySelector('.js-large-grid')
            ?.querySelectorAll('.bundle-configurator__patch-card')
            .forEach((c) => {
              const gid = c.dataset.groupId;
              c.classList.toggle('bundle-configurator__patch-card--selected', selectedGroupIds.has(gid));
              c.classList.toggle('bundle-configurator__patch-card--extra', extraGroupIds.has(gid));
            });
          this._updateLargeMaxState(stepEl);
          this._refreshSlots(stepEl);
          this._updateSectionVisibility(stepEl);
          this._checkStep2Continue(stepEl);
          this._updateStep2Total(stepEl);
        });
      } else {
        const miniIdx = i - mood.large_count;
        removeBtn.addEventListener('click', () => {
          if (!this._state.selectedMinis[miniIdx]) return;
          this._state.selectedMinis.splice(miniIdx, 1);
          const selectedGroupIds = new Set([
            ...this._state.selectedMinis.map((m) => m.groupId || String(m.id)),
            ...this._state.selectedExtraMiniPatches.map((m) => m.groupId || String(m.id)),
          ]);
          const extraGroupIds = new Set(this._state.selectedExtraMiniPatches.map((m) => m.groupId || String(m.id)));
          stepEl.querySelector('.js-small-grid')
            ?.querySelectorAll('.bundle-configurator__patch-card')
            .forEach((c) => {
              const gid = c.dataset.groupId;
              c.classList.toggle('bundle-configurator__patch-card--selected', selectedGroupIds.has(gid));
              c.classList.toggle('bundle-configurator__patch-card--extra', extraGroupIds.has(gid));
            });
          this._updateMiniMaxState(stepEl);
          this._refreshSlots(stepEl);
          this._checkStep2Continue(stepEl);
          this._updateStep2Total(stepEl);
        });
      }

      slotsEl.appendChild(frag);
    }
  }

  _refreshSlots(stepEl) {
    const mood = this._state.selectedMood;
    if (!mood) return;
    const largeSlots = this._asideEl.querySelectorAll('.bundle-configurator__patch-slot[data-slot-type="large"]');
    const smallSlots = this._asideEl.querySelectorAll('.bundle-configurator__patch-slot[data-slot-type="small"]');
    for (let i = 0; i < mood.large_count; i++) {
      this._fillSlot(largeSlots[i], this._state.selectedLargePatches[i] || null);
    }
    for (let i = 0; i < mood.small_count; i++) {
      this._fillSlot(smallSlots[i], this._state.selectedMinis[i] || null);
    }
    this._refreshExtraSlots(stepEl);
  }

  _fillSlot(slot, product, alwaysShowRemove = false) {
    if (!slot) return;
    slot._bcProduct = product || null;
    const iconEl   = slot.querySelector('.bundle-configurator__patch-slot-icon');
    const imgEl    = slot.querySelector('.bundle-configurator__patch-slot-img');
    const removeEl = slot.querySelector('.bundle-configurator__patch-slot-remove');
    if (product) {
      slot.classList.remove('bundle-configurator__patch-slot--empty');
      slot.classList.add('bundle-configurator__patch-slot--filled');
      iconEl.hidden = true;
      if (removeEl) removeEl.hidden = false;
      if (product.image) {
        imgEl.src    = product.image;
        imgEl.alt    = product.displayTitle;
        imgEl.hidden = false;
      } else {
        iconEl.textContent = '✓';
        iconEl.hidden      = false;
        imgEl.hidden       = true;
      }
    } else {
      slot.classList.add('bundle-configurator__patch-slot--empty');
      slot.classList.remove('bundle-configurator__patch-slot--filled');
      imgEl.removeAttribute('src');
      imgEl.hidden       = true;
      iconEl.textContent = '?';
      iconEl.hidden      = false;
      if (removeEl) removeEl.hidden = !alwaysShowRemove;
    }
  }

  // ── Patch grids ───────────────────────────────────────────────────────────

  _renderTagFilters(products, filterEl, refreshFn) {
    filterEl.innerHTML = '';
    const tags = [...new Set(products.flatMap((p) => p.tags))].filter(Boolean).sort();
    if (tags.length === 0) return;

    const makeChip = (text, tag) => {
      const frag = document.importNode(
        document.getElementById('tmpl-bc-filter-chip').content, true
      );
      const chip = frag.querySelector('.bundle-configurator__filter-chip');
      chip.textContent  = text;
      chip.dataset.tag  = tag;
      chip.addEventListener('click', () => {
        filterEl.querySelectorAll('.bundle-configurator__filter-chip').forEach((c) =>
          c.classList.toggle('bundle-configurator__filter-chip--active', c === chip)
        );
        refreshFn();
      });
      return { frag, chip };
    };

    const { frag: allFrag, chip: allChip } = makeChip('All', '');
    allChip.classList.add('bundle-configurator__filter-chip--active');
    filterEl.appendChild(allFrag);

    tags.forEach((tag) => filterEl.appendChild(makeChip(tag, tag).frag));
  }

  _refreshLargeGrid(stepEl) {
    const mood   = this._state.selectedMood;
    const gridEl = stepEl.querySelector('.js-large-grid');
    const active = stepEl.querySelector('.js-large-filters .bundle-configurator__filter-chip--active');
    const tag    = active?.dataset.tag || '';
    let prods    = this._dedupeProducts(mood.large_collections.flatMap((h) => this._state.patchesCache[h] || []));
    if (tag) prods = prods.filter((p) => p.tags.includes(tag));
    this._renderPatchCards(prods, gridEl, true, stepEl);
    this._updateLargeMaxState(stepEl);
  }

  _refreshSmallGrid(stepEl) {
    const mood   = this._state.selectedMood;
    const gridEl = stepEl.querySelector('.js-small-grid');
    const active = stepEl.querySelector('.js-small-filters .bundle-configurator__filter-chip--active');
    const tag    = active?.dataset.tag || '';
    let prods    = this._dedupeProducts(mood.small_collections.flatMap((h) => this._state.patchesCache[h] || []));
    if (tag) prods = prods.filter((p) => p.tags.includes(tag));
    this._renderPatchCards(prods, gridEl, false, stepEl);
    this._updateMiniMaxState(stepEl);
  }

  _dedupeProducts(products) {
    const seen = new Set();
    return products.filter((p) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }

  _showGridSkeleton(gridEl, count = 8) {
    if (!gridEl) return;
    gridEl.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const card = document.createElement('div');
      card.className = 'bundle-configurator__patch-card bundle-configurator__patch-card--skeleton';
      card.innerHTML =
        '<div class="bundle-configurator__patch-card-media bc-skeleton"></div>' +
        '<div class="bc-skeleton bc-skeleton--text"></div>' +
        '<div class="bc-skeleton bc-skeleton--text bc-skeleton--short"></div>';
      gridEl.appendChild(card);
    }
  }

  _buildPatchGroups(product) {
    const BUNDLE = 'Bundle';
    const EXTRA  = 'Extra';
    const retailVal = this._bcRetailVal; // 'Retail'

    const bcOpt    = product.options?.find((o) => o.name === '_bc_price');
    const bcOptKey = bcOpt ? `option${bcOpt.position}` : null;
    const nonBcKeys = (product.options || [])
      .filter((o) => o.name !== '_bc_price')
      .map((o) => `option${o.position}`);

    if (!bcOptKey) {
      return product.variants.map((v) => {
        const isDefault  = v.title === 'Default Title';
        const displayTitle = isDefault ? product.title : `${product.title} / ${v.title}`;
        return {
          groupId:         String(v.id),
          displayTitle,
          description:     product.description || '',
          image:           v.image || product.featured_image,
          media:           product.media || [],
          tags:            product.tags,
          retailPrice:     v.price,
          bundlePrice:     v.price,
          extraPrice:      v.price,
          bundleVariantId: v.id,
          extraVariantId:  v.id,
          available:       v.available,
          price:           v.price,
        };
      });
    }

    const grouped = new Map();
    product.variants.forEach((v) => {
      const key = nonBcKeys.map((k) => v[k] || '').join('|');
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(v);
    });

    const groups = [];
    grouped.forEach((variants) => {
      const retailV = variants.find((v) => v[bcOptKey] === retailVal);
      const bundleV = variants.find((v) => v[bcOptKey] === BUNDLE);
      const extraV  = variants.find((v) => v[bcOptKey] === EXTRA);
      if (!bundleV && !retailV) return;

      const base        = retailV || bundleV;
      const optParts    = nonBcKeys.map((k) => base[k] || '').filter((s) => s && s !== 'Default Title');
      const displayTitle = optParts.length ? `${product.title} / ${optParts.join(' / ')}` : product.title;

      groups.push({
        groupId:         String(base.id),
        displayTitle,
        description:     product.description || '',
        image:           base.image || product.featured_image,
        media:           product.media || [],
        tags:            product.tags,
        retailPrice:     retailV?.price ?? bundleV?.price ?? 0,
        bundlePrice:     bundleV?.price ?? retailV?.price ?? 0,
        extraPrice:      extraV?.price ?? bundleV?.price ?? 0,
        bundleVariantId: bundleV?.id || retailV?.id,
        extraVariantId:  extraV?.id  || bundleV?.id,
        available:       (bundleV?.available ?? false) && (!retailV || retailV.available),
        price:           bundleV?.price ?? retailV?.price ?? 0,
      });
    });
    return groups;
  }

  _renderPatchCards(products, gridEl, isLarge, stepEl) {
    gridEl.innerHTML = '';
    const tmpl = document.getElementById('tmpl-bc-patch-card');

    products.forEach((product) => {
      const groups = this._buildPatchGroups(product);

      groups.forEach((patchItem) => {
        const frag = document.importNode(tmpl.content, true);
        const card = frag.querySelector('.bundle-configurator__patch-card');
        const img  = card.querySelector('.bundle-configurator__patch-card-img');

        if (patchItem.image) {
          img.src = patchItem.image;
          img.alt = patchItem.displayTitle;
        } else {
          img.hidden = true;
        }

        card.dataset.groupId = patchItem.groupId;
        card.querySelector('.bundle-configurator__patch-card-title').textContent = patchItem.displayTitle;

        const priceEl = card.querySelector('.bundle-configurator__patch-card-price');
        const hasTiers = patchItem.bundlePrice !== patchItem.retailPrice || patchItem.extraPrice !== patchItem.retailPrice;
        if (hasTiers) {
          priceEl.innerHTML =
            `<del class="bc-price-retail">${bcFormatMoney(patchItem.retailPrice, this._currency)}</del>` +
            `<span class="bc-price-bundle">${bcFormatMoney(patchItem.bundlePrice, this._currency)}</span>` +
            `<span class="bc-price-extra">${bcFormatMoney(patchItem.extraPrice, this._currency)}</span>`;
        } else {
          priceEl.textContent = bcFormatMoney(patchItem.price, this._currency);
        }

        this._wireCardInfo(frag, patchItem);

        const inBundle = isLarge
          ? this._state.selectedLargePatches.some((p) => p.groupId === patchItem.groupId)
          : this._state.selectedMinis.some((m) => m.groupId === patchItem.groupId);
        const inExtra  = isLarge
          ? this._state.selectedExtraLargePatches.some((p) => p.groupId === patchItem.groupId)
          : this._state.selectedExtraMiniPatches.some((m) => m.groupId === patchItem.groupId);
        if (inBundle || inExtra) card.classList.add('bundle-configurator__patch-card--selected');
        if (inExtra)             card.classList.add('bundle-configurator__patch-card--extra');

        if (!patchItem.available) card.classList.add('bundle-configurator__patch-card--unavailable');

        card.addEventListener('click', () => {
          if (!patchItem.available) return;
          const wasMaxed = card.classList.contains('bundle-configurator__patch-card--maxed');
          if (isLarge) {
            this._selectLargePatch(patchItem, stepEl);
          } else {
            this._toggleMini(patchItem, stepEl);
          }
          if (wasMaxed) {
            this._shakeExtraBtn(stepEl, 'large');
            this._shakeExtraBtn(stepEl, 'mini');
          } else {
            this._renderGallery(patchItem);
          }
        });

        gridEl.appendChild(frag);
      });
    });
  }

  // ── Section visibility ────────────────────────────────────────────────────

  // Scrolls the content column back to the top of the step (used when the visible
  // patch section flips, e.g. maxi done → mini shown, so filters aren't cut off)
  _scrollStepTop() {
    const body = this.querySelector('.bundle-configurator__body');
    if (body) body.scrollTo({ top: 0, behavior: 'smooth' });
    if (this._panelsEl) this._panelsEl.scrollTo({ top: 0, behavior: 'smooth' });
  }

  _updateSectionVisibility(stepEl) {
    const mood = this._state.selectedMood;
    if (!mood) return;
    const largeSection = stepEl.querySelector('.js-large-section');
    const smallSection = stepEl.querySelector('.js-small-section');
    const prevLargeHidden = largeSection.hidden;
    const prevSmallHidden = smallSection.hidden;
    const hasLarge     = mood.large_count > 0 && mood.large_collections.length > 0;
    const hasSmall     = mood.small_count > 0 && mood.small_collections.length > 0;
    const hasExtraLarge = this._state.extraLargeSlotCount > 0 && mood.large_collections.length > 0;
    const hasExtraMini  = this._state.extraMiniSlotCount  > 0 && mood.small_collections.length > 0;

    if (hasLarge || hasExtraLarge) {
      if (hasSmall || hasExtraMini) {
        // Both sides exist — sequential: large first, then small
        const requiredLargeDone = mood.large_count === 0 ||
          this._state.selectedLargePatches.length === mood.large_count;
        const extraLargeNotFull = hasExtraLarge &&
          this._state.selectedExtraLargePatches.length < this._state.extraLargeSlotCount;
        largeSection.hidden = requiredLargeDone && !extraLargeNotFull;
        smallSection.hidden = !requiredLargeDone;
      } else {
        // Large only
        largeSection.hidden = false;
        smallSection.hidden = true;
      }
    } else {
      // Small only (or extra mini only)
      largeSection.hidden = true;
      smallSection.hidden = !hasSmall && !hasExtraMini;
    }

    // The visible section flipped (maxi ↔ mini) — show the step from the top again
    if (largeSection.hidden !== prevLargeHidden || smallSection.hidden !== prevSmallHidden) {
      this._scrollStepTop();
    }
  }

  // ── Patch selection ───────────────────────────────────────────────────────

  _selectLargePatch(product, stepEl) {
    const mood     = this._state.selectedMood;
    const gid      = product.groupId || String(product.id);
    const reqIdx   = this._state.selectedLargePatches.findIndex((p) => (p.groupId || String(p.id)) === gid);
    const extraIdx = this._state.selectedExtraLargePatches.findIndex((p) => (p.groupId || String(p.id)) === gid);

    if (reqIdx >= 0) {
      this._state.selectedLargePatches.splice(reqIdx, 1);
    } else if (extraIdx >= 0) {
      this._state.selectedExtraLargePatches.splice(extraIdx, 1);
    } else if (this._state.selectedLargePatches.length < mood.large_count) {
      this._state.selectedLargePatches.push(product);
    } else if (this._state.selectedExtraLargePatches.length < this._state.extraLargeSlotCount) {
      this._state.selectedExtraLargePatches.push(product);
    }

    const selectedGids = new Set([
      ...this._state.selectedLargePatches.map((p) => p.groupId || String(p.id)),
      ...this._state.selectedExtraLargePatches.map((p) => p.groupId || String(p.id)),
    ]);
    const extraGids = new Set(this._state.selectedExtraLargePatches.map((p) => p.groupId || String(p.id)));
    stepEl.querySelector('.js-large-grid')
      ?.querySelectorAll('.bundle-configurator__patch-card')
      .forEach((c) => {
        c.classList.toggle('bundle-configurator__patch-card--selected', selectedGids.has(c.dataset.groupId));
        c.classList.toggle('bundle-configurator__patch-card--extra', extraGids.has(c.dataset.groupId));
      });

    this._updateLargeMaxState(stepEl);
    this._refreshSlots(stepEl);
    this._updateSectionVisibility(stepEl);
    this._checkStep2Continue(stepEl);
    this._updateStep2Total(stepEl);
  }

  _toggleMini(product, stepEl) {
    const mood     = this._state.selectedMood;
    const gid      = product.groupId || String(product.id);
    const reqIdx   = this._state.selectedMinis.findIndex((m) => (m.groupId || String(m.id)) === gid);
    const extraIdx = this._state.selectedExtraMiniPatches.findIndex((m) => (m.groupId || String(m.id)) === gid);

    if (reqIdx >= 0) {
      this._state.selectedMinis.splice(reqIdx, 1);
    } else if (extraIdx >= 0) {
      this._state.selectedExtraMiniPatches.splice(extraIdx, 1);
    } else if (this._state.selectedMinis.length < mood.small_count) {
      this._state.selectedMinis.push(product);
    } else if (this._state.selectedExtraMiniPatches.length < this._state.extraMiniSlotCount) {
      this._state.selectedExtraMiniPatches.push(product);
    }

    const selectedGids = new Set([
      ...this._state.selectedMinis.map((m) => m.groupId || String(m.id)),
      ...this._state.selectedExtraMiniPatches.map((m) => m.groupId || String(m.id)),
    ]);
    const extraGids = new Set(this._state.selectedExtraMiniPatches.map((m) => m.groupId || String(m.id)));
    stepEl.querySelector('.js-small-grid')
      ?.querySelectorAll('.bundle-configurator__patch-card')
      .forEach((c) => {
        c.classList.toggle('bundle-configurator__patch-card--selected', selectedGids.has(c.dataset.groupId));
        c.classList.toggle('bundle-configurator__patch-card--extra', extraGids.has(c.dataset.groupId));
      });

    this._updateMiniMaxState(stepEl);
    this._refreshSlots(stepEl);
    this._checkStep2Continue(stepEl);
    this._updateStep2Total(stepEl);
  }

  _updateLargeMaxState(stepEl) {
    const mood = this._state.selectedMood;
    if (!mood) return;
    const totalSelected = this._state.selectedLargePatches.length + this._state.selectedExtraLargePatches.length;
    const totalCap      = mood.large_count + this._state.extraLargeSlotCount;
    // --maxed: all slots full, click does nothing (greyed out)
    const allFull       = totalSelected >= totalCap;
    // --price-extra: bundle slots full → any new pick goes to extra → show extra price
    const bundleFull    = this._state.selectedLargePatches.length >= mood.large_count;
    const selectedGids  = new Set([
      ...this._state.selectedLargePatches.map((p) => p.groupId || String(p.id)),
      ...this._state.selectedExtraLargePatches.map((p) => p.groupId || String(p.id)),
    ]);
    const extraGids     = new Set(this._state.selectedExtraLargePatches.map((p) => p.groupId || String(p.id)));
    stepEl.querySelector('.js-large-grid')
      ?.querySelectorAll('.bundle-configurator__patch-card')
      .forEach((c) => {
        const gid        = c.dataset.groupId;
        const isSelected = selectedGids.has(gid);
        const isExtra    = extraGids.has(gid);
        c.classList.toggle('bundle-configurator__patch-card--maxed',      allFull    && !isSelected);
        c.classList.toggle('bundle-configurator__patch-card--extra',      isExtra);
        c.classList.toggle('bundle-configurator__patch-card--price-extra', bundleFull && !isSelected && !isExtra);
      });
  }

  _updateMiniMaxState(stepEl) {
    const mood = this._state.selectedMood;
    if (!mood) return;
    const totalSelected = this._state.selectedMinis.length + this._state.selectedExtraMiniPatches.length;
    const totalCap      = mood.small_count + this._state.extraMiniSlotCount;
    const allFull       = totalSelected >= totalCap;
    const bundleFull    = this._state.selectedMinis.length >= mood.small_count;
    const selectedGids  = new Set([
      ...this._state.selectedMinis.map((m) => m.groupId || String(m.id)),
      ...this._state.selectedExtraMiniPatches.map((m) => m.groupId || String(m.id)),
    ]);
    const extraGids     = new Set(this._state.selectedExtraMiniPatches.map((m) => m.groupId || String(m.id)));
    stepEl.querySelector('.js-small-grid')
      ?.querySelectorAll('.bundle-configurator__patch-card')
      .forEach((c) => {
        const gid        = c.dataset.groupId;
        const isSelected = selectedGids.has(gid);
        const isExtra    = extraGids.has(gid);
        c.classList.toggle('bundle-configurator__patch-card--maxed',       allFull    && !isSelected);
        c.classList.toggle('bundle-configurator__patch-card--extra',       isExtra);
        c.classList.toggle('bundle-configurator__patch-card--price-extra', bundleFull && !isSelected && !isExtra);
      });
  }

  // ── Step 2 footer ─────────────────────────────────────────────────────────

  _checkStep2Continue(stepEl) {
    const mood = this._state.selectedMood;
    const btn  = this._panelEl.querySelector('.js-step2-continue');
    if (!btn || !mood) return;
    const largeFilled = mood.large_count === 0 || this._state.selectedLargePatches.length === mood.large_count;
    const smallFilled = mood.small_count === 0 || this._state.selectedMinis.length === mood.small_count;
    btn.disabled = !(largeFilled && smallFilled);
  }

  _updateStep2Total(stepEl) {
    const totalEl   = this._panelEl.querySelector('.js-total');
    const retailEl  = this._panelEl.querySelector('.js-retail-total');
    const savingsEl = this._panelEl.querySelector('.js-savings');
    if (!totalEl) return;

    let bundleCents = 0;
    let retailCents = 0;

    if (this._state.selectedBaseVariant) {
      bundleCents += this._state.selectedBaseVariant.price;
      retailCents += this._state.selectedBaseVariant.price;
    }
    this._state.selectedLargePatches.forEach((p) => {
      bundleCents += p.bundlePrice ?? p.price;
      retailCents += p.retailPrice ?? p.price;
    });
    this._state.selectedMinis.forEach((m) => {
      bundleCents += m.bundlePrice ?? m.price;
      retailCents += m.retailPrice ?? m.price;
    });
    this._state.selectedExtraLargePatches.forEach((p) => {
      bundleCents += p.extraPrice ?? p.price;
      retailCents += p.retailPrice ?? p.price;
    });
    this._state.selectedExtraMiniPatches.forEach((m) => {
      bundleCents += m.extraPrice ?? m.price;
      retailCents += m.retailPrice ?? m.price;
    });

    totalEl.textContent = bundleCents > 0 ? bcFormatMoney(bundleCents, this._currency) : '';

    const savings = retailCents - bundleCents;
    if (retailEl) retailEl.textContent = savings > 0 ? bcFormatMoney(retailCents, this._currency) : '';
    if (savingsEl) {
      savingsEl.hidden      = savings <= 0;
      savingsEl.innerHTML = savings > 0
        ? `${this._t.save || 'Save'} ${bcFormatMoney(savings, this._currency)} <span class="bc-pct-badge">-${Math.round(savings / retailCents * 100)}%</span>`
        : '';
    }
    this._updateShippingBar();
  }

  _onStep2Continue() {
    const mood = this._state.selectedMood;
    if (!mood) return;

    this._state.cartItems = this._state.cartItems.filter(
      (i) => i._slot !== 'mood' && !i._slot.startsWith('mood_') &&
             !i._slot.startsWith('mini_') && !i._slot.startsWith('extra_')
    );

    this._state.selectedLargePatches.forEach((patch, i) => {
      this._state.cartItems.push({
        _slot:      i === 0 ? 'mood' : `mood_${i}`,
        id:         patch.bundleVariantId || patch.id,
        quantity:   1,
        properties: { _patch_title: patch.displayTitle },
      });
    });

    this._state.selectedMinis.forEach((mini, i) => {
      this._state.cartItems.push({
        _slot:      `mini_${i}`,
        id:         mini.bundleVariantId || mini.id,
        quantity:   1,
        properties: { _mini_title: mini.displayTitle },
      });
    });

    this._state.selectedExtraLargePatches.forEach((patch, i) => {
      this._state.cartItems.push({
        _slot:      `extra_large_${i}`,
        id:         patch.extraVariantId || patch.id,
        quantity:   1,
        properties: { _patch_title: patch.displayTitle },
      });
    });

    this._state.selectedExtraMiniPatches.forEach((mini, i) => {
      this._state.cartItems.push({
        _slot:      `extra_mini_${i}`,
        id:         mini.extraVariantId || mini.id,
        quantity:   1,
        properties: { _mini_title: mini.displayTitle },
      });
    });

    this._goToStep(3);
  }

  // ── Extra slots ───────────────────────────────────────────

  _addExtraLargeSlot(stepEl) {
    const max = this._state.selectedMood?.max_extra_large ?? 5;
    if (this._state.extraLargeSlotCount >= max) return;
    this._state.extraLargeSlotCount++;
    this._renderExtraSlots(stepEl);
    this._updateExtraBtnStates(stepEl);
    this._updateLargeMaxState(stepEl);
    this._updateSectionVisibility(stepEl);
  }

  _addExtraMiniSlot(stepEl) {
    const max = this._state.selectedMood?.max_extra_small ?? 5;
    if (this._state.extraMiniSlotCount >= max) return;
    this._state.extraMiniSlotCount++;
    this._renderExtraSlots(stepEl);
    this._updateExtraBtnStates(stepEl);
    this._updateMiniMaxState(stepEl);
    this._updateSectionVisibility(stepEl);
  }

  _updateExtraBtnStates(stepEl) {
    const mood     = this._state.selectedMood;
    const maxLarge = mood?.max_extra_large ?? 5;
    const maxSmall = mood?.max_extra_small ?? 5;
    // querySelectorAll — sync both the aside buttons and the mobile duplicate
    const largeDisabled = this._state.extraLargeSlotCount >= maxLarge || !(mood?.large_collections?.length > 0);
    const miniDisabled  = this._state.extraMiniSlotCount  >= maxSmall || !(mood?.small_collections?.length > 0);
    this.querySelectorAll('.js-add-extra-large').forEach((b) => { b.disabled = largeDisabled; });
    this.querySelectorAll('.js-add-extra-mini').forEach((b)  => { b.disabled = miniDisabled; });
  }

  _shakeExtraBtn(stepEl, type) {
    const sel = type === 'large' ? '.js-add-extra-large' : '.js-add-extra-mini';
    const CLS = 'bundle-configurator__extra-btn--shake';
    this.querySelectorAll(sel).forEach((btn) => {
      if (btn.disabled) return;
      btn.classList.remove(CLS);
      void btn.offsetWidth; // reflow to restart animation
      btn.classList.add(CLS);
      btn.addEventListener('animationend', () => btn.classList.remove(CLS), { once: true });
    });
  }

  _shakeExtraAddsBtn(stepEl) {
    const CLS = 'bundle-configurator__extra-btn--shake';
    this.querySelectorAll('.js-add-extra-adds').forEach((btn) => {
      if (btn.disabled) return;
      btn.classList.remove(CLS);
      void btn.offsetWidth;
      btn.classList.add(CLS);
      btn.addEventListener('animationend', () => btn.classList.remove(CLS), { once: true });
    });
  }

  _renderExtraSlots(stepEl) {
    const slotsEl = this._asideEl.querySelector('.js-patch-slots');
    slotsEl.querySelectorAll(
      '.bundle-configurator__patch-slot[data-slot-type="extra-large"],' +
      '.bundle-configurator__patch-slot[data-slot-type="extra-mini"],' +
      '.bundle-configurator__patch-slot-sep--extra'
    ).forEach((el) => el.remove());

    const totalExtra = this._state.extraLargeSlotCount + this._state.extraMiniSlotCount;
    if (totalExtra === 0) return;

    const sep = document.createElement('span');
    sep.className   = 'bundle-configurator__patch-slot-sep bundle-configurator__patch-slot-sep--extra';
    sep.textContent = '+';
    slotsEl.appendChild(sep);

    const slotTmpl = document.getElementById('tmpl-bc-patch-slot');

    const makeSlot = (type, idx, onRemove) => {
      const frag      = document.importNode(slotTmpl.content, true);
      const slot      = frag.querySelector('.bundle-configurator__patch-slot');
      const removeBtn = slot.querySelector('.bundle-configurator__patch-slot-remove');
      slot.dataset.slotType  = type;
      slot.dataset.slotGroup = 'mood';
      slot.dataset.slotIdx   = idx;
      slot.classList.add('bundle-configurator__patch-slot--extra');
      removeBtn.hidden = false;
      removeBtn.addEventListener('click', onRemove);
      slot.addEventListener('click', (e) => {
        if (e.target.closest('.bundle-configurator__patch-slot-remove')) return;
        if (slot._bcProduct) { this._renderGallery(slot._bcProduct); this._scrollToMedia(); }
      });
      return frag;
    };

    for (let i = 0; i < this._state.extraLargeSlotCount; i++) {
      const idx = i;
      slotsEl.appendChild(makeSlot('extra-large', idx, () => {
        this._state.selectedExtraLargePatches.splice(idx, 1);
        this._state.extraLargeSlotCount--;
        this._state.selectedExtraLargePatches =
          this._state.selectedExtraLargePatches.slice(0, this._state.extraLargeSlotCount);
        const allGids  = new Set([
          ...this._state.selectedLargePatches.map((p) => p.groupId || String(p.id)),
          ...this._state.selectedExtraLargePatches.map((p) => p.groupId || String(p.id)),
        ]);
        const extraGids = new Set(this._state.selectedExtraLargePatches.map((p) => p.groupId || String(p.id)));
        stepEl.querySelector('.js-large-grid')
          ?.querySelectorAll('.bundle-configurator__patch-card')
          .forEach((c) => {
            c.classList.toggle('bundle-configurator__patch-card--selected', allGids.has(c.dataset.groupId));
            c.classList.toggle('bundle-configurator__patch-card--extra', extraGids.has(c.dataset.groupId));
          });
        this._updateLargeMaxState(stepEl);
        this._renderExtraSlots(stepEl);
        this._updateExtraBtnStates(stepEl);
        this._updateSectionVisibility(stepEl);
        this._checkStep2Continue(stepEl);
        this._updateStep2Total(stepEl);
      }));
    }

    for (let i = 0; i < this._state.extraMiniSlotCount; i++) {
      const idx = i;
      slotsEl.appendChild(makeSlot('extra-mini', idx, () => {
        this._state.selectedExtraMiniPatches.splice(idx, 1);
        this._state.extraMiniSlotCount--;
        this._state.selectedExtraMiniPatches =
          this._state.selectedExtraMiniPatches.slice(0, this._state.extraMiniSlotCount);
        const allGids  = new Set([
          ...this._state.selectedMinis.map((m) => m.groupId || String(m.id)),
          ...this._state.selectedExtraMiniPatches.map((m) => m.groupId || String(m.id)),
        ]);
        const extraGids = new Set(this._state.selectedExtraMiniPatches.map((m) => m.groupId || String(m.id)));
        stepEl.querySelector('.js-small-grid')
          ?.querySelectorAll('.bundle-configurator__patch-card')
          .forEach((c) => {
            c.classList.toggle('bundle-configurator__patch-card--selected', allGids.has(c.dataset.groupId));
            c.classList.toggle('bundle-configurator__patch-card--extra', extraGids.has(c.dataset.groupId));
          });
        this._updateMiniMaxState(stepEl);
        this._renderExtraSlots(stepEl);
        this._updateExtraBtnStates(stepEl);
        this._checkStep2Continue(stepEl);
        this._updateStep2Total(stepEl);
      }));
    }

    this._refreshExtraSlots(stepEl);
  }

  _refreshExtraSlots(stepEl) {
    const mood = this._state.selectedMood;
    if (!mood) return;
    const extraLargeSlots = this._asideEl.querySelectorAll('.bundle-configurator__patch-slot[data-slot-type="extra-large"]');
    const extraMiniSlots  = this._asideEl.querySelectorAll('.bundle-configurator__patch-slot[data-slot-type="extra-mini"]');
    for (let i = 0; i < this._state.extraLargeSlotCount; i++) {
      this._fillSlot(extraLargeSlots[i], this._state.selectedExtraLargePatches[i] || null, true);
    }
    for (let i = 0; i < this._state.extraMiniSlotCount; i++) {
      this._fillSlot(extraMiniSlots[i], this._state.selectedExtraMiniPatches[i] || null, true);
    }
  }

  // ── Step 3 render ─────────────────────────────────────────────────────────

  async _renderStep3() {
    const config = this._data.adds_config;

    if (!config || !config.adds_collections.length) {
      this._goToStep(4);
      return;
    }

    const tmpl = document.getElementById('tmpl-bc-step3');
    if (!tmpl) return;
    const frag   = document.importNode(tmpl.content, true);
    const stepEl = frag.querySelector('.bundle-configurator__step--3');

    this._wireSharedBack(2);

    const continueBtn = stepEl.querySelector('.js-step3-continue');
    const btn3Cfg = this._data.step_buttons?.['3'] || {};
    continueBtn.innerHTML = `${btn3Cfg.text || this._t.continue || 'Continue'} <span class="svg-wrapper">${_BC_ARROW_SVG}</span>`;
    this._styleStepBtn(continueBtn, btn3Cfg);
    continueBtn.addEventListener('click', () => this._onStep3Continue());

    const extraAddsBtn = stepEl.querySelector('.js-add-extra-adds');
    if (extraAddsBtn) extraAddsBtn.addEventListener('click', () => this._addExtraAddsSlot(stepEl));

    // Inject skip button next to the step description in the header
    const skipSlot = this.querySelector('.js-step-skip-slot');
    if (skipSlot) {
      const skip = document.createElement('button');
      skip.type = 'button';
      skip.className = 'bundle-configurator__skip-btn';
      // Two labels: compact "Skip →" on mobile, full text on desktop (CSS toggles)
      const skipM = document.createElement('span');
      skipM.className = 'bc-skip-label-m';
      skipM.textContent = `${this._data.skip_label || 'Skip'} →`;
      const skipD = document.createElement('span');
      skipD.className = 'bc-skip-label-d';
      skipD.textContent = this._data.skip_label_desktop || this._t.skip_adds || "I don't want adds";
      skip.append(skipM, skipD);
      skip.addEventListener('click', () => this._onStep3Skip());
      skipSlot.innerHTML = '';
      skipSlot.appendChild(skip);
    }

    this._panelsEl.appendChild(frag);

    // Distribute movable blocks across regions (like step 2)
    this._relocateToRegions(stepEl, {
      slots:   '.js-step3-slots-row',
      extra:   '.js-step3-extra',
      primary: '.js-step3-primary',
      extraBtns: [
        ['.js-add-extra-adds', () => this._addExtraAddsSlot(stepEl)],
      ],
    });

    const stepLabel = this.querySelector('.js-step-label');
    if (stepLabel) stepLabel.textContent = this._t.choose_adds || 'The adds';

    this._renderStep3ReadonlySlots(stepEl);
    this._renderAddsSlots(stepEl);
    this._updateExtraAddsBtnState(stepEl);
    this._updateStep3Total(stepEl);
    this._showGridSkeleton(stepEl.querySelector('.js-adds-grid'));

    try {
      await this._fetchPatches(config.adds_collections);
    } catch (e) {
      console.error('[BC] Step 3 fetch error:', e);
      return;
    }

    const prods = this._dedupeProducts(
      config.adds_collections.flatMap((h) => this._state.patchesCache[h] || [])
    );

    this._renderTagFilters(prods, stepEl.querySelector('.js-adds-filters'), () => this._refreshAddsGrid(stepEl));
    this._renderAddsCards(prods, stepEl.querySelector('.js-adds-grid'), stepEl);
    this._refreshAddsSlots(stepEl);
  }

  _renderAddsSlots(stepEl) {
    const config  = this._data.adds_config;
    if (!config) return;
    const slotsEl = this._asideEl.querySelector('.js-adds-slots');
    slotsEl.innerHTML = '';
    const slotTmpl = document.getElementById('tmpl-bc-patch-slot');

    for (let i = 0; i < config.adds_count; i++) {
      const frag      = document.importNode(slotTmpl.content, true);
      const slot      = frag.querySelector('.bundle-configurator__patch-slot');
      const removeBtn = slot.querySelector('.bundle-configurator__patch-slot-remove');
      slot.dataset.slotIdx   = i;
      slot.dataset.slotType  = 'add';
      slot.dataset.slotGroup = 'add';

      removeBtn.addEventListener('click', () => {
        if (!this._state.selectedAdds[i]) return;
        this._state.selectedAdds.splice(i, 1);
        const selectedGids = new Set([
          ...this._state.selectedAdds.map((a) => a.groupId || String(a.id)),
          ...this._state.selectedExtraAdds.map((a) => a.groupId || String(a.id)),
        ]);
        const extraGids = new Set(this._state.selectedExtraAdds.map((a) => a.groupId || String(a.id)));
        stepEl.querySelector('.js-adds-grid')
          ?.querySelectorAll('.bundle-configurator__patch-card')
          .forEach((c) => {
            c.classList.toggle('bundle-configurator__patch-card--selected', selectedGids.has(c.dataset.groupId));
            c.classList.toggle('bundle-configurator__patch-card--extra',    extraGids.has(c.dataset.groupId));
          });
        this._updateAddsMaxState(stepEl);
        this._refreshAddsSlots(stepEl);
        this._updateStep3Total(stepEl);
      });

      slot.addEventListener('click', (e) => {
        if (e.target.closest('.bundle-configurator__patch-slot-remove')) return;
        if (slot._bcProduct) { this._renderGallery(slot._bcProduct); this._scrollToMedia(); }
      });

      slotsEl.appendChild(frag);
    }

    this._renderExtraAddsSlots(stepEl);
  }

  _refreshAddsSlots(stepEl) {
    const config = this._data.adds_config;
    if (!config) return;
    const slots = this._asideEl.querySelectorAll('.js-adds-slots .bundle-configurator__patch-slot[data-slot-type="add"]');
    for (let i = 0; i < config.adds_count; i++) {
      this._fillSlot(slots[i], this._state.selectedAdds[i] || null);
    }
    this._refreshExtraAddsSlots(stepEl);
  }

  _renderExtraAddsSlots(stepEl) {
    const slotsEl = this._asideEl.querySelector('.js-adds-slots');
    slotsEl.querySelectorAll(
      '.bundle-configurator__patch-slot[data-slot-type="extra-add"],' +
      '.bundle-configurator__patch-slot-sep--extra'
    ).forEach((el) => el.remove());

    if (this._state.extraAddsSlotCount === 0) return;

    const sep = document.createElement('span');
    sep.className = 'bundle-configurator__patch-slot-sep bundle-configurator__patch-slot-sep--extra';
    sep.textContent = '+';
    slotsEl.appendChild(sep);

    const slotTmpl = document.getElementById('tmpl-bc-patch-slot');
    for (let idx = 0; idx < this._state.extraAddsSlotCount; idx++) {
      const frag      = document.importNode(slotTmpl.content, true);
      const slot      = frag.querySelector('.bundle-configurator__patch-slot');
      const removeBtn = slot.querySelector('.bundle-configurator__patch-slot-remove');
      slot.dataset.slotType  = 'extra-add';
      slot.dataset.slotGroup = 'add';
      slot.classList.add('bundle-configurator__patch-slot--extra');

      removeBtn.addEventListener('click', () => {
        this._state.selectedExtraAdds.splice(idx, 1);
        this._state.extraAddsSlotCount = Math.max(0, this._state.extraAddsSlotCount - 1);
        this._state.selectedExtraAdds = this._state.selectedExtraAdds.slice(0, this._state.extraAddsSlotCount);
        const allGids  = new Set([
          ...this._state.selectedAdds.map((a) => a.groupId || String(a.id)),
          ...this._state.selectedExtraAdds.map((a) => a.groupId || String(a.id)),
        ]);
        const extraGids = new Set(this._state.selectedExtraAdds.map((a) => a.groupId || String(a.id)));
        stepEl.querySelector('.js-adds-grid')
          ?.querySelectorAll('.bundle-configurator__patch-card')
          .forEach((c) => {
            c.classList.toggle('bundle-configurator__patch-card--selected', allGids.has(c.dataset.groupId));
            c.classList.toggle('bundle-configurator__patch-card--extra',    extraGids.has(c.dataset.groupId));
          });
        this._renderExtraAddsSlots(stepEl);
        this._refreshExtraAddsSlots(stepEl);
        this._updateAddsMaxState(stepEl);
        this._updateExtraAddsBtnState(stepEl);
        this._updateStep3Total(stepEl);
      });

      slot.addEventListener('click', (e) => {
        if (e.target.closest('.bundle-configurator__patch-slot-remove')) return;
        if (slot._bcProduct) { this._renderGallery(slot._bcProduct); this._scrollToMedia(); }
      });

      slotsEl.appendChild(frag);
    }
  }

  _refreshExtraAddsSlots(stepEl) {
    const slots = this._asideEl.querySelectorAll('.bundle-configurator__patch-slot[data-slot-type="extra-add"]');
    slots.forEach((slot, i) => {
      this._fillSlot(slot, this._state.selectedExtraAdds[i] || null, true);
    });
  }

  _addExtraAddsSlot(stepEl) {
    const max = this._data.adds_config?.max_extra ?? 5;
    if (this._state.extraAddsSlotCount >= max) return;
    this._state.extraAddsSlotCount++;
    this._renderExtraAddsSlots(stepEl);
    this._refreshExtraAddsSlots(stepEl);
    this._updateExtraAddsBtnState(stepEl);
    this._updateAddsMaxState(stepEl);
  }

  _updateExtraAddsBtnState(stepEl) {
    const max = this._data.adds_config?.max_extra ?? 5;
    const disabled = this._state.extraAddsSlotCount >= max;
    // querySelectorAll — sync the aside button and the mobile duplicate
    this.querySelectorAll('.js-add-extra-adds').forEach((b) => { b.disabled = disabled; });
  }

  _renderAddsCards(products, gridEl, stepEl) {
    gridEl.innerHTML = '';
    const tmpl = document.getElementById('tmpl-bc-patch-card');

    products.forEach((product) => {
      const groups = this._buildPatchGroups(product);

      groups.forEach((patchItem) => {
        const frag = document.importNode(tmpl.content, true);
        const card = frag.querySelector('.bundle-configurator__patch-card');
        const img  = card.querySelector('.bundle-configurator__patch-card-img');

        if (patchItem.image) { img.src = patchItem.image; img.alt = patchItem.displayTitle; }
        else { img.hidden = true; }

        card.dataset.groupId = patchItem.groupId;
        card.querySelector('.bundle-configurator__patch-card-title').textContent = patchItem.displayTitle;

        const priceEl  = card.querySelector('.bundle-configurator__patch-card-price');
        const hasTiers = patchItem.bundlePrice !== patchItem.retailPrice || patchItem.extraPrice !== patchItem.retailPrice;
        if (hasTiers) {
          priceEl.innerHTML =
            `<del class="bc-price-retail">${bcFormatMoney(patchItem.retailPrice, this._currency)}</del>` +
            `<span class="bc-price-bundle">${bcFormatMoney(patchItem.bundlePrice, this._currency)}</span>` +
            `<span class="bc-price-extra">${bcFormatMoney(patchItem.extraPrice, this._currency)}</span>`;
        } else {
          priceEl.textContent = bcFormatMoney(patchItem.price, this._currency);
        }

        this._wireCardInfo(frag, patchItem);

        const inBundle = this._state.selectedAdds.some((a) => (a.groupId || String(a.id)) === patchItem.groupId);
        const inExtra  = this._state.selectedExtraAdds.some((a) => (a.groupId || String(a.id)) === patchItem.groupId);
        if (inBundle || inExtra) card.classList.add('bundle-configurator__patch-card--selected');
        if (inExtra)             card.classList.add('bundle-configurator__patch-card--extra');

        if (!patchItem.available) card.classList.add('bundle-configurator__patch-card--unavailable');

        card.addEventListener('click', () => {
          if (!patchItem.available) return;
          const wasMaxed = card.classList.contains('bundle-configurator__patch-card--maxed');
          this._toggleAdd(patchItem, stepEl);
          if (wasMaxed) {
            this._shakeExtraAddsBtn(stepEl);
          } else {
            this._renderGallery(patchItem);
          }
        });

        gridEl.appendChild(frag);
      });
    });

    this._updateAddsMaxState(stepEl);
  }

  _toggleAdd(product, stepEl) {
    const config   = this._data.adds_config;
    const gid      = product.groupId || String(product.id);
    const reqIdx   = this._state.selectedAdds.findIndex((a) => (a.groupId || String(a.id)) === gid);
    const extraIdx = this._state.selectedExtraAdds.findIndex((a) => (a.groupId || String(a.id)) === gid);

    if (reqIdx >= 0) {
      this._state.selectedAdds.splice(reqIdx, 1);
    } else if (extraIdx >= 0) {
      this._state.selectedExtraAdds.splice(extraIdx, 1);
    } else if (this._state.selectedAdds.length < config.adds_count) {
      this._state.selectedAdds.push(product);
    } else if (this._state.selectedExtraAdds.length < this._state.extraAddsSlotCount) {
      this._state.selectedExtraAdds.push(product);
    }

    const selectedGids = new Set([
      ...this._state.selectedAdds.map((a) => a.groupId || String(a.id)),
      ...this._state.selectedExtraAdds.map((a) => a.groupId || String(a.id)),
    ]);
    const extraGids = new Set(this._state.selectedExtraAdds.map((a) => a.groupId || String(a.id)));
    stepEl.querySelector('.js-adds-grid')
      ?.querySelectorAll('.bundle-configurator__patch-card')
      .forEach((c) => {
        c.classList.toggle('bundle-configurator__patch-card--selected', selectedGids.has(c.dataset.groupId));
        c.classList.toggle('bundle-configurator__patch-card--extra',    extraGids.has(c.dataset.groupId));
      });

    this._updateAddsMaxState(stepEl);
    this._refreshAddsSlots(stepEl);
    this._updateStep3Total(stepEl);
  }

  _updateAddsMaxState(stepEl) {
    const config = this._data.adds_config;
    if (!config) return;
    const totalSelected = this._state.selectedAdds.length + this._state.selectedExtraAdds.length;
    const totalCap      = config.adds_count + this._state.extraAddsSlotCount;
    const allFull       = totalSelected >= totalCap;
    const bundleFull    = this._state.selectedAdds.length >= config.adds_count;
    const selectedGids  = new Set([
      ...this._state.selectedAdds.map((a) => a.groupId || String(a.id)),
      ...this._state.selectedExtraAdds.map((a) => a.groupId || String(a.id)),
    ]);
    const extraGids = new Set(this._state.selectedExtraAdds.map((a) => a.groupId || String(a.id)));
    stepEl.querySelector('.js-adds-grid')
      ?.querySelectorAll('.bundle-configurator__patch-card')
      .forEach((c) => {
        const gid        = c.dataset.groupId;
        const isSelected = selectedGids.has(gid);
        const isExtra    = extraGids.has(gid);
        c.classList.toggle('bundle-configurator__patch-card--maxed',       allFull    && !isSelected);
        c.classList.toggle('bundle-configurator__patch-card--extra',       isExtra);
        c.classList.toggle('bundle-configurator__patch-card--price-extra', bundleFull && !isSelected && !isExtra);
      });
  }

  _refreshAddsGrid(stepEl) {
    const config = this._data.adds_config;
    if (!config) return;
    const gridEl = stepEl.querySelector('.js-adds-grid');
    const active = stepEl.querySelector('.js-adds-filters .bundle-configurator__filter-chip--active');
    const tag    = active?.dataset.tag || '';
    let prods    = this._dedupeProducts(config.adds_collections.flatMap((h) => this._state.patchesCache[h] || []));
    if (tag) prods = prods.filter((p) => p.tags.includes(tag));
    this._renderAddsCards(prods, gridEl, stepEl);
  }

  _updateStep3Total(stepEl) {
    const totalEl   = this._panelEl.querySelector('.js-total');
    const retailEl  = this._panelEl.querySelector('.js-retail-total');
    const savingsEl = this._panelEl.querySelector('.js-savings');
    if (!totalEl) return;

    let bundleCents = 0;
    let retailCents = 0;

    if (this._state.selectedBaseVariant) {
      bundleCents += this._state.selectedBaseVariant.price;
      retailCents += this._state.selectedBaseVariant.price;
    }
    this._state.selectedLargePatches.forEach((p) => {
      bundleCents += p.bundlePrice ?? p.price;
      retailCents += p.retailPrice ?? p.price;
    });
    this._state.selectedMinis.forEach((m) => {
      bundleCents += m.bundlePrice ?? m.price;
      retailCents += m.retailPrice ?? m.price;
    });
    this._state.selectedExtraLargePatches.forEach((p) => {
      bundleCents += p.extraPrice ?? p.price;
      retailCents += p.retailPrice ?? p.price;
    });
    this._state.selectedExtraMiniPatches.forEach((m) => {
      bundleCents += m.extraPrice ?? m.price;
      retailCents += m.retailPrice ?? m.price;
    });
    // Adds from step 3
    this._state.selectedAdds.forEach((a) => {
      bundleCents += a.bundlePrice ?? a.price;
      retailCents += a.retailPrice ?? a.price;
    });
    this._state.selectedExtraAdds.forEach((a) => {
      bundleCents += a.extraPrice ?? a.price;
      retailCents += a.retailPrice ?? a.price;
    });

    totalEl.textContent = bundleCents > 0 ? bcFormatMoney(bundleCents, this._currency) : '';

    const savings = retailCents - bundleCents;
    if (retailEl) retailEl.textContent = savings > 0 ? bcFormatMoney(retailCents, this._currency) : '';
    if (savingsEl) {
      savingsEl.hidden      = savings <= 0;
      savingsEl.innerHTML = savings > 0
        ? `${this._t.save || 'Save'} ${bcFormatMoney(savings, this._currency)} <span class="bc-pct-badge">-${Math.round(savings / retailCents * 100)}%</span>`
        : '';
    }
    this._updateShippingBar();
  }

  _setShippingBarLock(locked) {
    this.querySelectorAll('free-shipping-bar[data-bundle-mode]').forEach((bar) => {
      if (locked) bar.lock?.();
      else        bar.unlock?.();
    });
  }

  _updateShippingBar() {
    const bars = this.querySelectorAll('free-shipping-bar[data-bundle-mode]');
    if (!bars.length) return;

    let bundleCents = this._state.selectedBaseVariant?.price || 0;
    (this._state.selectedLargePatches      || []).forEach((p) => { bundleCents += p.bundlePrice ?? p.price; });
    (this._state.selectedMinis             || []).forEach((m) => { bundleCents += m.bundlePrice ?? m.price; });
    (this._state.selectedExtraLargePatches || []).forEach((p) => { bundleCents += p.extraPrice  ?? p.price; });
    (this._state.selectedExtraMiniPatches  || []).forEach((m) => { bundleCents += m.extraPrice  ?? m.price; });
    (this._state.selectedAdds              || []).forEach((a) => { bundleCents += a.bundlePrice ?? a.price; });
    (this._state.selectedExtraAdds         || []).forEach((a) => { bundleCents += a.extraPrice  ?? a.price; });

    bars.forEach((bar) => {
      if (typeof bar.updateWithExtra === 'function') bar.updateWithExtra(bundleCents);
    });
  }

  _onStep3Skip() {
    this._state.selectedAdds       = [];
    this._state.selectedExtraAdds  = [];
    this._state.extraAddsSlotCount = 0;
    this._state.cartItems = this._state.cartItems.filter((i) => !i._slot.startsWith('add_'));
    this._updateShippingBar();
    this._goToStep(4);
  }

  _onStep3Continue() {
    this._state.cartItems = this._state.cartItems.filter((i) => !i._slot.startsWith('add_'));
    this._state.selectedAdds.forEach((add, i) => {
      this._state.cartItems.push({
        _slot:      `add_${i}`,
        id:         add.bundleVariantId || add.id,
        quantity:   1,
        properties: { _add_title: add.displayTitle },
      });
    });
    this._state.selectedExtraAdds.forEach((add, i) => {
      this._state.cartItems.push({
        _slot:      `add_extra_${i}`,
        id:         add.extraVariantId || add.id,
        quantity:   1,
        properties: { _add_title: add.displayTitle },
      });
    });
    this._goToStep(4);
  }

  // ── Step 4 render ─────────────────────────────────────────────────────────

  _renderStep4() {
    const tmpl = document.getElementById('tmpl-bc-step4');
    if (!tmpl) return;
    const frag   = document.importNode(tmpl.content, true);
    const stepEl = frag.querySelector('.bundle-configurator__step--4');

    this._wireSharedBack(3, () => this._setShippingBarLock(false));

    const atcCfg = this._data.step_buttons?.['4_atc'] || {};
    const atcBtn = stepEl.querySelector('.js-step4-atc');
    atcBtn.innerHTML = `
      <span class="add-to-cart-text">
        <span class="svg-wrapper add-to-cart-icon">${_BC_ATC_ICON}</span>
        <span class="add-to-cart-text__content">${atcCfg.text || this._t.add_to_cart || 'Add to cart'}</span>
      </span>
      <span aria-hidden="true" class="add-to-cart-text--added">
        <span class="svg-wrapper add-to-cart-icon--added">${_BC_CHECK_SVG}</span>
        <span>Added</span>
      </span>`;
    this._styleStepBtn(atcBtn, atcCfg);
    atcBtn.addEventListener('click', () => this._onAddToCart(stepEl));

    // Secure checkout — adds the bundle and redirects straight to /checkout
    const checkoutCfg = this._data.step_buttons?.['4_checkout'] || {};
    const checkoutBtn = stepEl.querySelector('.js-step4-checkout');
    if (checkoutBtn && checkoutCfg.enabled) {
      checkoutBtn.hidden = false;
      checkoutBtn.innerHTML = `${checkoutCfg.text || 'Secure checkout'} <span class="svg-wrapper">${_BC_ARROW_SVG}</span>`;
      this._styleStepBtn(checkoutBtn, checkoutCfg);
      checkoutBtn.addEventListener('click', () => this._onSecureCheckout(stepEl));
    }

    this._panelsEl.appendChild(frag);

    // ATC + Secure checkout → panel
    this._relocateToRegions(stepEl, { primary: '.js-step4-primary' });

    // Bundle composition next to the review (desktop). Remove buttons are a
    // section setting; default is view-only.
    this._renderCompositionAside(!!this._data.step4_removable);

    const stepLabel = this.querySelector('.js-step-label');
    if (stepLabel) stepLabel.textContent = this._t.check_your_box || 'Check your box';

    this._updateMedia(this._data.product.featured_image, this._data.product.title);
    this._renderReviewItems(stepEl);
    this._renderStep4Total(stepEl);
    this._setShippingBarLock(true);
  }

  _renderReviewItems(stepEl) {
    const container = stepEl.querySelector('.js-review-items');
    container.innerHTML = '';

    const base    = this._state.selectedBase;
    const variant = this._state.selectedBaseVariant;
    const isDefaultVariant = variant?.title === 'Default Title';
    const subtitle = isDefaultVariant ? null :
      base?.options?.map((opt) => `${opt.name}: ${variant.options[opt.position - 1]}`).join(' · ');

    this._appendReviewItem(container, {
      label:      'Base',
      image:      variant?.image || base?.featured_image,
      title:      base?.title || '',
      subtitle,
      price:      variant?.price || 0,
      largeMedia: true,
    });

    const mood = this._state.selectedMood;
    if (mood) {
      this._appendReviewItem(container, {
        label:    'Moods',
        image:    null,
        title:    mood.label,
        subtitle: null,
        price:    0,
        noBorder: true,
      });
    }

    this._state.selectedLargePatches.forEach((p) => {
      this._appendReviewItem(container, {
        label:       'Maxi mood',
        image:       p.image,
        title:       p.displayTitle,
        subtitle:    null,
        price:       p.bundlePrice ?? p.price,
        retailPrice: p.retailPrice ?? 0,
        nested:      true,
      });
    });

    this._state.selectedMinis.forEach((mini) => {
      this._appendReviewItem(container, {
        label:       'Mini mood',
        image:       mini.image,
        title:       mini.displayTitle,
        subtitle:    null,
        price:       mini.bundlePrice ?? mini.price,
        retailPrice: mini.retailPrice ?? 0,
        nested:      true,
      });
    });

    this._state.selectedExtraLargePatches.forEach((p) => {
      this._appendReviewItem(container, {
        label:       'Extra maxi mood',
        image:       p.image,
        title:       p.displayTitle,
        subtitle:    null,
        price:       p.extraPrice ?? p.price,
        retailPrice: p.retailPrice ?? 0,
        nested:      true,
      });
    });

    this._state.selectedExtraMiniPatches.forEach((m) => {
      this._appendReviewItem(container, {
        label:       'Extra mini mood',
        image:       m.image,
        title:       m.displayTitle,
        subtitle:    null,
        price:       m.extraPrice ?? m.price,
        retailPrice: m.retailPrice ?? 0,
        nested:      true,
      });
    });

    this._state.selectedAdds.forEach((add, i) => {
      this._appendReviewItem(container, {
        label:       'Add',
        image:       add.image,
        title:       add.displayTitle,
        subtitle:    null,
        price:       add.bundlePrice ?? add.price,
        retailPrice: add.retailPrice ?? 0,
        topBorder:   i === 0,
      });
    });

    this._state.selectedExtraAdds.forEach((add) => {
      this._appendReviewItem(container, {
        label:       'Extra add',
        image:       add.image,
        title:       add.displayTitle,
        subtitle:    null,
        price:       add.extraPrice ?? add.price,
        retailPrice: add.retailPrice ?? 0,
        nested:      true,
      });
    });
  }

  _appendReviewItem(container, { label, image, title, subtitle, price, retailPrice = 0, nested = false, noBorder = false, topBorder = false, largeMedia = false }) {
    const item = document.createElement('div');
    item.className = 'bundle-configurator__review-item'
      + (nested      ? ' bundle-configurator__review-item--nested'      : '')
      + (!image      ? ' bundle-configurator__review-item--no-media'    : '')
      + (noBorder    ? ' bundle-configurator__review-item--no-border'   : '')
      + (topBorder   ? ' bundle-configurator__review-item--top-border'  : '')
      + (largeMedia  ? ' bundle-configurator__review-item--large-media' : '');

    let media = null;
    if (image) {
      media = document.createElement('div');
      media.className = 'bundle-configurator__review-item-media';
      const img = document.createElement('img');
      img.src = image;
      img.alt = title;
      img.className = 'bundle-configurator__review-item-img';
      media.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'bundle-configurator__review-item-info';

    const labelEl = document.createElement('span');
    labelEl.className = 'bundle-configurator__review-item-label';
    labelEl.textContent = label;

    const titleEl = document.createElement('span');
    titleEl.className = 'bundle-configurator__review-item-title';
    titleEl.textContent = title;

    info.append(labelEl, titleEl);

    if (subtitle) {
      const subEl = document.createElement('span');
      subEl.className = 'bundle-configurator__review-item-meta';
      subEl.textContent = subtitle;
      info.appendChild(subEl);
    }

    const priceEl = document.createElement('span');
    priceEl.className = 'bundle-configurator__review-item-price';
    if (retailPrice > 0 && retailPrice !== price) {
      const pct = Math.round((retailPrice - price) / retailPrice * 100);
      priceEl.innerHTML =
        `<del class="bc-review-price-retail">${bcFormatMoney(retailPrice, this._currency)}</del>` +
        `<span class="bc-review-price-current">${bcFormatMoney(price, this._currency)}</span>` +
        `<span class="bc-pct-badge">-${pct}%</span>`;
    } else if (price > 0) {
      priceEl.textContent = bcFormatMoney(price, this._currency);
    }

    if (media) item.append(media);
    item.append(info, priceEl);
    container.appendChild(item);
  }

  _renderStep4Total(stepEl) {
    const totalEl   = this._panelEl.querySelector('.js-total');
    const retailEl  = this._panelEl.querySelector('.js-retail-total');
    const savingsEl = this._panelEl.querySelector('.js-savings');
    if (!totalEl) return;

    let bundleCents = 0;
    let retailCents = 0;

    // Base product price (the MoodBox itself)
    if (this._state.selectedBaseVariant) {
      bundleCents += this._state.selectedBaseVariant.price;
      retailCents += this._state.selectedBaseVariant.price;
    }
    // Individual patches — mood.price is NOT added (it equals patch sum, would double-count)
    this._state.selectedLargePatches.forEach((p) => {
      bundleCents += p.bundlePrice ?? p.price;
      retailCents += p.retailPrice ?? p.price;
    });
    this._state.selectedMinis.forEach((m) => {
      bundleCents += m.bundlePrice ?? m.price;
      retailCents += m.retailPrice ?? m.price;
    });
    this._state.selectedExtraLargePatches.forEach((p) => {
      bundleCents += p.extraPrice ?? p.price;
      retailCents += p.retailPrice ?? p.price;
    });
    this._state.selectedExtraMiniPatches.forEach((m) => {
      bundleCents += m.extraPrice ?? m.price;
      retailCents += m.retailPrice ?? m.price;
    });
    this._state.selectedAdds.forEach((a) => {
      bundleCents += a.bundlePrice ?? a.price;
      retailCents += a.retailPrice ?? a.price;
    });
    this._state.selectedExtraAdds.forEach((a) => {
      bundleCents += a.extraPrice ?? a.price;
      retailCents += a.retailPrice ?? a.price;
    });

    totalEl.textContent = bundleCents > 0 ? bcFormatMoney(bundleCents, this._currency) : '';

    const savings = retailCents - bundleCents;
    if (retailEl) retailEl.textContent = savings > 0 ? bcFormatMoney(retailCents, this._currency) : '';
    if (savingsEl) {
      savingsEl.hidden      = savings <= 0;
      savingsEl.innerHTML = savings > 0
        ? `${this._t.save || 'Save'} ${bcFormatMoney(savings, this._currency)} <span class="bc-pct-badge">-${Math.round(savings / retailCents * 100)}%</span>`
        : '';
    }
    this._updateShippingBar();
  }

  _computeRetailTotal() {
    let cents = 0;
    if (this._state.selectedBaseVariant) cents += this._state.selectedBaseVariant.price;
    this._state.selectedLargePatches.forEach(p => { cents += p.retailPrice ?? p.price; });
    this._state.selectedMinis.forEach(m => { cents += m.retailPrice ?? m.price; });
    this._state.selectedExtraLargePatches.forEach(p => { cents += p.retailPrice ?? p.price; });
    this._state.selectedExtraMiniPatches.forEach(m => { cents += m.retailPrice ?? m.price; });
    this._state.selectedAdds.forEach(a => { cents += a.retailPrice ?? a.price; });
    this._state.selectedExtraAdds.forEach(a => { cents += a.retailPrice ?? a.price; });
    return cents;
  }

  async _onAddToCart(stepEl) {
    const atcBtn  = this._panelEl.querySelector('.js-step4-atc');
    const errorEl = stepEl.querySelector('.js-atc-error');

    atcBtn.disabled = true;
    const atcTextEl = atcBtn.querySelector('.add-to-cart-text__content');
    if (atcTextEl) atcTextEl.textContent = this._t.adding || 'Adding…';
    errorEl.hidden = true;

    // Attach retail total + title to bundle parent item for cart rendering
    const retailTotal = this._computeRetailTotal();
    const bundleCartItem = this._state.cartItems.find(i => i._slot === 'bundle');
    if (bundleCartItem) {
      bundleCartItem.properties = {
        ...bundleCartItem.properties,
        _retail_total: retailTotal,
        _bundle_title: this._data.product.title || 'MoodBox',
      };
    }

    const bundleId = this._generateBundleId();
    const payload  = this._buildCartPayload(bundleId);

    try {
      const res  = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }

      // Horizon theme cart event (opens drawer if auto-open is set)
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail:  { resource: data, sourceId: 'bundle-configurator', data: {} },
      }));

      if (this._data.step5?.enabled) {
        this._renderStep5();
      } else {
        atcBtn.classList.add('atc-added');
        setTimeout(() => {
          atcBtn.classList.remove('atc-added');
          atcBtn.disabled = false;
          if (atcTextEl) atcTextEl.textContent = this._t.add_to_cart || 'Add to cart';
        }, 1500);
      }

    } catch (err) {
      atcBtn.disabled = false;
      if (atcTextEl) atcTextEl.textContent = this._t.add_to_cart || 'Add to cart';
      errorEl.textContent = err.message;
      errorEl.hidden      = false;
      console.error('[BC] Add to cart error:', err);
    }
  }

  // Secure checkout — adds the bundle to the cart and redirects straight to /checkout
  async _onSecureCheckout(stepEl) {
    const btn     = this._panelEl.querySelector('.js-step4-checkout');
    const atcBtn  = this._panelEl.querySelector('.js-step4-atc');
    const errorEl = stepEl.querySelector('.js-atc-error');

    const origHTML = btn ? btn.innerHTML : '';
    if (btn)    { btn.disabled = true; btn.textContent = this._t.adding || 'Adding…'; }
    if (atcBtn) atcBtn.disabled = true;
    if (errorEl) errorEl.hidden = true;

    // Attach retail total + title to bundle parent item (like in _onAddToCart)
    const retailTotal = this._computeRetailTotal();
    const bundleCartItem = this._state.cartItems.find(i => i._slot === 'bundle');
    if (bundleCartItem) {
      bundleCartItem.properties = {
        ...bundleCartItem.properties,
        _retail_total: retailTotal,
        _bundle_title: this._data.product.title || 'MoodBox',
      };
    }

    const bundleId = this._generateBundleId();
    const payload  = this._buildCartPayload(bundleId);

    try {
      const res  = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }
      window.location.href = '/checkout';
    } catch (err) {
      if (btn)    { btn.disabled = false; btn.innerHTML = origHTML; }
      if (atcBtn) atcBtn.disabled = false;
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      console.error('[BC] Secure checkout error:', err);
    }
  }

  _renderStep5() {
    const tmpl = document.getElementById('tmpl-bc-step5');
    if (!tmpl) return;

    this.querySelector('.js-step5')?.remove();

    const frag = document.importNode(tmpl.content, true);
    const el   = frag.querySelector('.js-step5');

    // btn2 — add another (new bundle)
    el.querySelector('.js-step5-btn2')?.addEventListener('click', () => {
      el.remove();
      this._resetForNewBundle();
      this._goToStep(1);
    });

    // btn4 — re-add same bundle
    const btn4 = el.querySelector('.js-step5-btn4');
    if (btn4) {
      btn4.addEventListener('click', async () => {
        btn4.disabled = true;
        const origText = btn4.textContent;
        btn4.textContent = '…';
        try {
          const bundleId = this._generateBundleId();
          const payload  = this._buildCartPayload(bundleId);
          const res  = await fetch('/cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body:    JSON.stringify(payload),
          });
          const data = await res.json();
          if (!res.ok || data.status) throw new Error(data.description || data.message || 'Error');
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail:  { resource: data, sourceId: 'bundle-configurator', data: {} },
          }));
          btn4.textContent = '✓';
          setTimeout(() => {
            btn4.textContent = origText;
            btn4.disabled = false;
          }, 1500);
        } catch (e) {
          btn4.textContent = origText;
          btn4.disabled = false;
          console.error('[BC] Re-add error:', e);
        }
      });
    }

    // close button → back to step 4
    el.querySelector('.js-step5-close').addEventListener('click', () => {
      el.remove();
      const atcBtn = this._panelEl.querySelector('.js-step4-atc');
      if (atcBtn) {
        atcBtn.disabled = false;
        const atcTextEl = atcBtn.querySelector('.add-to-cart-text__content');
        if (atcTextEl) atcTextEl.textContent = this._t.add_to_cart || 'Add to cart';
      }
      this.querySelectorAll('free-shipping-bar[data-bundle-mode]').forEach((bar) => {
        bar.unlock?.();
        bar.updateWithExtra?.(0);
      });
    });

    // Append to the section itself so it covers the WHOLE configurator (header, gallery,
    // content, panel) and does not scroll away inside the content column.
    this.appendChild(el);
  }

  _resetForNewBundle() {
    this._state.selectedBase              = null;
    this._state.selectedBaseVariant       = null;
    this._state.selectedMood              = null;
    this._state.selectedLargePatches      = [];
    this._state.selectedMinis             = [];
    this._state.selectedExtraLargePatches = [];
    this._state.selectedExtraMiniPatches  = [];
    this._state.selectedAdds              = [];
    this._state.selectedExtraAdds         = [];
    this._state.extraAddsSlotCount        = 0;
    this._state.cartItems                 = [
      {
        _slot:      'bundle',
        id:         this._data.product.default_variant_id,
        quantity:   1,
        properties: {},
      },
    ];
    this._state.optionSelections          = {};

    // Clear visual selection on step 1 cards
    const step1 = this._panelsEl?.querySelector('.bundle-configurator__step--1');
    if (step1) {
      step1.querySelectorAll('.bundle-configurator__base-card--selected')
        .forEach((c) => c.classList.remove('bundle-configurator__base-card--selected'));
      this.querySelectorAll('.bundle-configurator__option-chip--selected')
        .forEach((c) => c.classList.remove('bundle-configurator__option-chip--selected'));
      const continueBtn = this.querySelector('.js-continue-btn');
      if (continueBtn) continueBtn.disabled = true;
    }

    this.querySelectorAll('free-shipping-bar[data-bundle-mode]').forEach((bar) => {
      bar.unlock?.();
      bar.updateWithExtra?.(0);
    });
  }
}

customElements.define('bundle-configurator', BundleConfigurator);
